/**
 * Layout store — items + nextCC for the *currently active* project.
 *
 * Items and the rolling CC counter both live inside the project store now;
 * this module is a thin facade that keeps the existing component API
 * unchanged (`layoutItems()`, `addButton()`, etc.) while routing all reads
 * and writes through the active project.
 *
 * Editing mode is global (not per-project) — it's a UI mode, not data.
 */

import { batch, createSignal, createMemo, type Accessor } from 'solid-js';
import type { LfoWaveform, DmxTarget, AbletonStep } from '@opbuttons/schema';
import { DEFAULT_DMX_TARGET } from '@opbuttons/schema';
import { DEFAULT_STAGE_COLOR } from '../lighting/palette.ts';
import {
  activeProject,
  activePage,
  activeCanvasSize,
  setActiveItems,
  setActiveItemsAndNextCC,
  activePedals,
  cloneItemWithNewIds,
  addPedal,
} from './projectStore.ts';

// Re-export shared types so existing imports `from '../layout/store.ts'` keep
// working without touching their import paths.
export type {
  ButtonItem, LfoItem, LayoutItem, LayoutColor,
  ButtonGridItem, ButtonGridCell, CellColor,
  SequencerItem,
  StreamDeckItem,
  MacroItem,
  FaderItem, FaderResponse,
  RadialItem, LabelItem, PresetChangeItem,
  SnapshotChangeItem, SnapshotTarget,
  XYPadItem,
  TimelineLauncherItem, TimelineLauncherMode,
  PianoItem, PianoTouchMode,
  TransposeItem,
  SceneCopyItem,
  CapoItem,
  RecQuantItem,
  AbletonRowItem,
  WavesAutotuneItem,
  MixerItem,
  SizePreset,
  FaceOptions,
  ControlActions, ActionStep,
  ButtonShape,
  StatusLedItem, StatusLedSourceType, StatusLedShape,
  DisplayItem, DisplaySourceType, DisplayStyle,
  RigStateButtonItem,
  LightColorItem, LightLevelItem, LightFxItem, BlackoutItem,
  OpVisualTriggerItem, OpVisualGridItem, AudioPadsItem, AudioPad,
} from './projectStore.ts';
import { SCENE_COPY_COLUMNS, buttonEmitsMidi } from './projectStore.ts';
import { DEFAULT_REC_QUANT_LABELS } from './projectStore.ts';
import { REC_QUANT_LABELS, normalizeRecQuant, type RecQuantVia } from '@opbuttons/schema';
import {
  defaultRowCells, normaliseRowCells, rowControlForStep, rowCellsFromStep, type AbletonRowControl,
} from '../ableton/rows.ts';
import type { ModShareable, ButtonItem, LfoItem, LayoutItem, ButtonGridItem, ButtonGridCell, CellColor, SequencerItem, StreamDeckItem, MacroItem, FaderItem, XYPadItem, TimelineLauncherItem, PianoItem, TransposeItem, CapoItem, RecQuantItem, AbletonRowItem, SceneCopyItem, WavesAutotuneItem, MixerItem, SizePreset, ButtonShape, StatusLedItem, RadialItem, LabelItem, PresetChangeItem, SnapshotChangeItem, GuitarPedal, Page, DisplayItem, RigStateButtonItem, LightColorItem, LightLevelItem, LightFxItem, BlackoutItem, OpVisualTriggerItem, OpVisualGridItem, AudioPadsItem, AudioPad, PaletteHue, NameSize } from './projectStore.ts';
import { sizeFor, buttonSizeFor, clampsFor, buttonClampsFor } from './sizes.ts';
import { applyAspectRule } from './shapes.ts';
import { spawnSpot, nearestFreeShift, overlapsAny, boundsOf, type Guide, type Rect } from './placement.ts';
import { upsertSequencer, makeDrumSequencer } from '../sequencer/store.ts';
import { macros, upsertMacro } from '../macros/store.ts';
import { ulid } from '../utils/ulid.ts';
// OBS Computer auto-pick (2026-09-19). `obs/scenes.ts` is a leaf - it
// imports only solid-js - so this cannot cycle back through scenePoll,
// which is the module that imports THIS file.
import { autoPickObsBridge } from '../obs/scenes.ts';
// Same leaf-module arrangement for Run Program's auto-pick.
import { autoPickProgramBridge } from '../program/programs.ts';
import { autoPickPresetBridge } from '../lighting/presets.ts';
import { hueSceneEntries } from '../lighting/hueScenes.ts';
import { autoPickOpVisualBridge } from '../opvisual/machine.ts';
import { OPVISUAL_COLS, type OpVisualAction } from '../opvisual/wire.ts';
import { bridges } from '../opbridge/store.ts';
import type { Macro } from '@opbuttons/schema';
import { uiMode, setUiMode, isEditMode, isTestMode } from '../performance/store.ts';
import { editTriggersEnabled, controlGap } from '../settings/store.ts';
import { followItemClaimOf, dropClaimsOfItems } from '../matrix/store.ts';
import {
  reallocateMidi, flagMidiAllocFailure, clearMidiAllocFailure, patchTouchesMidi,
  type MidiSlot,
} from './midiAlloc.ts';

/** Default spawn size for all kinds. Resize via the EditableItem drag
 *  handle (clamps to [S, L] for the kind) or the size segmented control
 *  in the InspectorPanel. */
const DEFAULT_SIZE: SizePreset = 'M';

/** Rotation through the extended cell palette. Cell N gets palette[N % len].
 *  Order tuned so a 10-cell row reads as visually distinct genres. */
const CELL_COLOR_PALETTE: CellColor[] = [
  'cyan', 'magenta', 'amber', 'green',
  'violet', 'yellow', 'pink', 'sky', 'tan', 'red',
];

export const layoutItems: Accessor<LayoutItem[]> = createMemo(() => activePage()?.items ?? []);
export const nextCC: Accessor<number> = createMemo(() => activeProject()?.nextCC ?? 60);

// Mode is a projection of `uiMode()` from the UI-mode store; the boolean
// `isEditMode` / `isTestMode` readers live there and are
// re-exported here so existing `from '../layout/store.ts'` imports keep
// working unchanged. The transitions below are defined HERE (not in
// performance/store.ts) because they also clear the inspector selection — a
// layout concern — and keeping them here avoids an import cycle.
export { isEditMode, isTestMode, uiMode };

// ─── Hover-grab (edit mode, triggers-on) ─────────────────────────
// When "triggers in edit mode" is enabled, controls fire on tap; to move an
// item you hover it ~2s to GRAB it. While grabbed, the edit-mode wrapper
// owns the gesture (drag) and the control's trigger is suppressed. Only one
// item is grabbed at a time.
const [grabbedIdSig, setGrabbedIdSig] = createSignal<string | null>(null);
/** The item currently hover-grabbed for dragging, or null. */
export const grabbedItemId: Accessor<string | null> = grabbedIdSig;
export function setGrabbedItemId(id: string | null): void { setGrabbedIdSig(id); }

// ─── Move-arm (item context menu → "Move") ───────────────────────
// The right-click menu's Move row arms ONE item for dragging without
// changing mode and without selecting it. Two reasons it is its own signal
// rather than a reuse of either neighbour:
//
//   * not `selectItem` — selection opens the InspectorPanel, and "right-
//     click must not auto-open the inspector" is the whole point of the
//     menu. Move that pops the inspector would reintroduce it by the back
//     door.
//   * not `grabbedItemId` — hover-grab is cleared by pointerleave, and after
//     clicking a menu row the pointer is wherever the row was, which may be
//     off the item. An explicit choice must not evaporate on a mouse move.
//
// THE ARM IS STICKY (changed 2026-08-09). It used to be one arm = one move,
// cleared by the drop. Operator: *"when moving item, keep it movable until
// user clicks off the item"* - placing a control is a series of nudges, and
// a round trip through the right-click menu between each of them is not a
// placement workflow. So the drop no longer clears it. What does:
//   * a pointerdown anywhere that is not this item (`disarmOnPressOutside`
//     in App.tsx) - the primary, everyday exit;
//   * Esc, the "get me out of whatever surface I'm in" key;
//   * arming another item, deleting this one, toggling edit mode.
// An armed item wears the pulsing amber dashed outline throughout, which is
// what keeps a suppressed trigger from reading as a dead control. Not
// persisted - a reload starts disarmed.
const [moveArmedIdSig, setMoveArmedIdSig] = createSignal<string | null>(null);
/** The item explicitly armed for dragging via the context menu, or null. */
export const moveArmedItemId: Accessor<string | null> = moveArmedIdSig;
/** Arm this item for a drag. */
export function armItemMove(id: string | null): void {
  setMoveArmedIdSig(id);
}
/** Cancel the armed move, if any. */
export function disarmItemMove(): void { setMoveArmedIdSig(null); }

// ─── Pick-a-button mode (DisplayItem source=button, 2026-08-12) ──
// "Click on canvas" affordance for binding a DisplayItem to a Button item,
// alternative to the Inspector's <select>. Sticky like the move-arm above,
// for the same reason: a click on the target button may land nowhere near
// where the Inspector row that started picking is. Cleared by:
//   * clicking a button item on the canvas (resolves the pick);
//   * Esc, the standing "get me out" key;
//   * clicking anywhere that is not a pickable button (`cancelPickButton` in
//     EditableItem's wrapper pointerdown - a picking session is a commitment
//     to "pick or bail", not a mode you can putter around other items in);
//   * deleting the DisplayItem that started it, leaving edit mode.
const [pickButtonForSig, setPickButtonForSig] = createSignal<string | null>(null);
/** The DisplayItem id currently picking a Button from the canvas, or null. */
export const pickButtonForDisplayId: Accessor<string | null> = pickButtonForSig;
/** Start picking: the canvas dims everything but Button items until one is
 *  clicked or the pick is cancelled. */
export function startPickButtonForDisplay(displayItemId: string): void {
  setPickButtonForSig(displayItemId);
}
/** Cancel the in-flight pick, if any. */
export function cancelPickButton(): void { setPickButtonForSig(null); }

/**
 * True when the wrapper (not the child control) owns a tap on this item —
 * i.e. the control should NOT fire, because the tap selects / drags.
 *
 * - Armed for a menu-driven Move: always true, in test mode too. That is
 *   what makes Move usable without flipping the global EDIT toggle, and it
 *   is why every control that already calls this suppresses its trigger for
 *   the duration of an armed move for free.
 * - Otherwise outside edit mode: false (controls fire; test-mode drag is the
 *   separate DEC-054 selected-item path in EditableItem).
 * - Edit mode, triggers-OFF (default): always true — tap selects / drags,
 *   preserving the long-standing behaviour.
 * - Edit mode, triggers-ON: true ONLY while this item is hover-grabbed, so
 *   an un-grabbed item fires for in-place testing.
 *
 * Controls call this in their tap handlers instead of a bare `isEditMode()`.
 * With the default (triggers-off, nothing armed) it is identical to
 * `isEditMode()`.
 */
export function editOwnsGesture(itemId: string): boolean {
  if (moveArmedIdSig() === itemId) return true;
  if (!isEditMode()) return false;
  if (!editTriggersEnabled()) return true;
  return grabbedIdSig() === itemId;
}

/** Toggle between edit and test. Leaving edit mode clears the inspector
 *  selection so test mode doesn't carry an invisible selected-item.
 *  Performance mode does not block this (DEC-216): it is a background
 *  switch, and the performer is never locked out of fixing something. */
export function toggleEditMode(): void {
  const next = uiMode() === 'edit' ? 'test' : 'edit';
  setUiMode(next);
  setMoveArmedIdSig(null);
  setPickButtonForSig(null);
  if (next !== 'edit') clearSelection();
}

// Ratio-lock — a session-wide UI intent, not per-item state. When ON, all
// resize paths (Dimensions inputs + all 8 drag zones) preserve the item's
// w:h ratio at the moment the operation started. When OFF, sides are
// single-axis and inputs are independent (the default from CP-044).
// Shift held during a drag temporarily inverts the lock, matching Figma
// muscle memory. Not persisted — carries across item selection during
// this session, resets on reload.
const [ratioLocked, setRatioLocked] = createSignal(false);
export const isRatioLocked = ratioLocked;
export function toggleRatioLock(): void {
  setRatioLocked(!ratioLocked());
}

// ─── SELECTION (grown from one id to a set, 2026-08-09) ──────────
//
// This retires the CP-024 "multi-select is future, not now" decision. It was
// deferred for want of a use case; the operator now has one - highlight
// several controls and act on all of them from the right-click menu. The
// single-id signal was deliberately shaped for exactly this day, and the
// change lands where that note predicted: `selectItem`, EditableItem's one
// read, and the inspector's consumer.
//
// AN ORDERED ARRAY, NOT A SET, and the order is load-bearing: the LAST id is
// the inspector's target, which is what makes *"inspector will only open the
// one that was last right clicked on"* expressible at all. A `Set` would
// technically preserve insertion order too, but nothing about a Set says the
// order means something, and the next person would happily re-sort it.
//
// MULTI-SELECT IS EDIT-MODE ONLY (the gestures, not this store). Test mode
// keeps exactly the single-select behaviour it has always had, because a tap
// there fires a control on a rig that gets gigged - a mode where taps
// silently accumulate a selection is not something to hand a performer. The
// store stays mode-agnostic; the gates live in the gesture handlers.
const [selectedIdsSig, setSelectedIdsSig] = createSignal<string[]>([]);

/** Every highlighted item, in the order they were added. */
export const selectedItemIds: Accessor<string[]> = selectedIdsSig;

export function isItemSelected(id: string): boolean {
  return selectedIdsSig().includes(id);
}

/** The inspector's target - the most recently added to the selection. Kept
 *  as a single-id accessor because a dozen call sites ask "is this THE item
 *  the editor is pointed at", which is still a one-item question. */
export const selectedItemId: Accessor<string | null> = createMemo(() => {
  const ids = selectedIdsSig();
  return ids.length > 0 ? ids[ids.length - 1]! : null;
});

// HIGHLIGHTED IS NOT THE SAME AS BEING EDITED, and keeping them as one thing
// is contradictory now. Two rules from the operator collide otherwise:
//   * *"right click on items shouldn't auto bring up inspector"* (and later,
//     *"right click should highlight the item"*) - so a right-click must be
//     able to highlight WITHOUT opening the panel;
//   * a plain click in edit mode must still open it, as it always has.
// So the panel binds to its own target id, which only the gestures that mean
// "edit this" ever set. Every other selection change clears it.
const [inspectorTargetSig, setInspectorTargetSig] = createSignal<string | null>(null);

/** Replace the selection with this one item (or clear it) AND point the
 *  inspector at it. The plain, unmodified click, and the menu's Inspect row.
 *
 *  ONE update, not two (2026-10-10, operator: "when clicking between items
 *  the inspector flashes off and back on again"): between the two writes the
 *  selection named the new item and the target the old one, so
 *  `inspectorItem` was null for an instant, the panel's <Show> tore it down
 *  and built it again, and its entrance fade replayed. The fade between two
 *  items is App.tsx's. */
export function selectItem(id: string | null): void {
  batch(() => {
    setSelectedIdsSig(id === null ? [] : [id]);
    setInspectorTargetSig(id);
  });
}

/** Replace the selection with this one item and leave the inspector CLOSED.
 *  What a right-click does in edit mode: it has to collapse the selection so
 *  the menu acts on one unambiguous thing, without summoning the panel the
 *  menu exists to keep out of the way. */
export function highlightItem(id: string): void {
  setSelectedIdsSig([id]);
  setInspectorTargetSig(null);
}

/** Ctrl+click: add if absent, remove if present. Re-adding moves the id to
 *  the end, so the inspector target follows the most recent click. */
export function toggleItemSelected(id: string): void {
  const ids = selectedIdsSig();
  setSelectedIdsSig(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  setInspectorTargetSig(null);
}

/** Marquee result. De-duped, order preserved. */
export function setSelectedItems(ids: readonly string[]): void {
  setSelectedIdsSig([...new Set(ids)]);
  setInspectorTargetSig(null);
}

export function clearSelection(): void {
  setSelectedIdsSig([]);
  setInspectorTargetSig(null);
}

/** Convenience reader — the inspector-target item, or null. Tracks the active
 *  page's items so it auto-clears when that item is removed or the page
 *  changes. */
export const selectedItem: Accessor<LayoutItem | null> = createMemo(() => {
  const id = selectedItemId();
  if (!id) return null;
  return layoutItems().find((it) => it.id === id) ?? null;
});

/** What the InspectorPanel binds to: the selected item ONLY when exactly one
 *  is selected. A panel full of one item's CC/channel/name fields, shown
 *  while five items are highlighted and every menu action would hit all five,
 *  is an invitation to edit the wrong thing. Multi-select therefore closes
 *  the inspector, and the menu's Inspect row re-opens it by collapsing the
 *  selection back to one. */
export const inspectorItem: Accessor<LayoutItem | null> = createMemo(() => {
  const ids = selectedIdsSig();
  const target = inspectorTargetSig();
  // Both conditions matter: exactly one highlighted (the panel edits one
  // item), and that one is the item a "please edit this" gesture actually
  // named (so a right-click's quiet highlight cannot pop the panel).
  if (ids.length !== 1 || target === null || ids[0] !== target) return null;
  return layoutItems().find((it) => it.id === target) ?? null;
});

/** The selected items that actually exist on the ACTIVE page, in selection
 *  order. Group actions run through this rather than raw ids: a selection can
 *  outlive a page switch, and acting on an id that isn't here would silently
 *  do nothing on some items and not others. */
export const selectedItemsOnPage: Accessor<LayoutItem[]> = createMemo(() => {
  const items = layoutItems();
  return selectedIdsSig()
    .map((id) => items.find((it) => it.id === id))
    .filter((it): it is LayoutItem => it !== undefined);
});

export function moveItem(id: string, x: number, y: number): void {
  const pg = activePage();
  if (!pg) return;
  setActiveItems(pg.items.map((it) => (it.id === id ? { ...it, x, y } : it)));
}

/**
 * Commit a group drag: every id shifts by the SAME (dx, dy), so the set keeps
 * its shape. One store write for the whole group rather than N calls to
 * `moveItem`, because N writes would push N entries onto the undo stack and
 * make un-doing one drag take five presses.
 */
export function moveItems(deltas: ReadonlyMap<string, { x: number; y: number }>): void {
  const pg = activePage();
  if (!pg || deltas.size === 0) return;
  setActiveItems(pg.items.map((it) => {
    const at = deltas.get(it.id);
    return at ? { ...it, x: at.x, y: at.y } : it;
  }));
}

/**
 * Arrow-key nudge (2026-10-10): move every highlighted item on this page by
 * (dx, dy) design px, as a set, kept on the canvas. A nudge that would push
 * the set onto another control is refused rather than half-done, unless the
 * set already overlapped something (the same exemption a drag gets, so a
 * stacked pair can be nudged apart). Returns whether anything moved.
 */
export function nudgeSelection(dx: number, dy: number): boolean {
  const set = selectedItemsOnPage();
  if (set.length === 0) return false;
  const bounds = usableCanvasBounds() ?? activeCanvasSize();
  const ids = new Set(set.map((it) => it.id));
  const x0 = Math.min(...set.map((it) => it.x));
  const y0 = Math.min(...set.map((it) => it.y));
  const x1 = Math.max(...set.map((it) => it.x + it.w));
  const y1 = Math.max(...set.map((it) => it.y + it.h));
  const cx = bounds ? Math.max(-x0, Math.min(bounds.w - x1, dx)) : dx;
  const cy = bounds ? Math.max(-y0, Math.min(bounds.h - y1, dy)) : dy;
  if (cx === 0 && cy === 0) return false;
  const moved = set.map((it) => ({ id: it.id, x: it.x + cx, y: it.y + cy, w: it.w, h: it.h }));
  if (!groupWouldOverlap(set, ids) && groupWouldOverlap(moved, ids)) return false;
  moveItems(new Map(moved.map((m) => [m.id, { x: m.x, y: m.y }])));
  return true;
}

/** Remove several items in one write - same undo-granularity reasoning as
 *  `moveItems`, and the same reason the menu's Delete row calls this rather
 *  than looping `removeItem`. */
export function removeItems(ids: readonly string[]): void {
  const pg = activePage();
  if (!pg || ids.length === 0) return;
  const doomed = new Set(ids);
  setActiveItems(pg.items.filter((it) => !doomed.has(it.id)));
  setSelectedIdsSig(selectedIdsSig().filter((s) => !doomed.has(s)));
  if (moveArmedIdSig() && doomed.has(moveArmedIdSig()!)) setMoveArmedIdSig(null);
  if (grabbedIdSig() && doomed.has(grabbedIdSig()!)) setGrabbedIdSig(null);
  for (const id of ids) clearMidiAllocFailure(id);
  dropClaimsOfItems(ids);
}

/** Commit freeform-drag resize. The drag handle clamps to [S, L] for the
 *  kind before calling this. Doesn't change `size` (the spawn intent
 *  preset) — w/h diverge from the preset until the user clicks an S/M/L
 *  pill in the popover. ButtonGrid resize is handled by resizeButtonGrid
 *  (rows × cols) instead — drag-resize is disabled for grids. */
export function resizeItem(id: string, w: number, h: number): void {
  const pg = activePage();
  if (!pg) return;
  setActiveItems(pg.items.map((it) => (it.id === id ? { ...it, w, h } : it)));
}

/** Commit resize from a non-SE handle — top or left edge dragged means the
 *  item's x/y also shifted. Atomic write so live-drag doesn't flicker
 *  through an inconsistent (old x, new w) frame. */
export function moveAndResizeItem(
  id: string, x: number, y: number, w: number, h: number,
): void {
  const pg = activePage();
  if (!pg) return;
  setActiveItems(pg.items.map((it) => (it.id === id ? { ...it, x, y, w, h } : it)));
}

/** Union of every per-kind partial patch. Union (not intersection) so
 *  adding a new kind with disjoint fields (e.g. Fader.vertical, XYPad.
 *  xCcNumber) doesn't collapse existing fields to `never`. Internal
 *  editItem() probes via a Record<string, unknown> view so it can read
 *  common CC-related fields (ccNumber, baseCC, rows, cols) without
 *  narrowing to a specific kind. */
export type ItemPatch =
  | ModShareable
  | Partial<Omit<ButtonItem, 'id' | 'kind'>>
  | Partial<Omit<LfoItem, 'id' | 'kind'>>
  | Partial<Omit<ButtonGridItem, 'id' | 'kind'>>
  | Partial<Omit<StreamDeckItem, 'id' | 'kind'>>
  | Partial<Omit<MacroItem, 'id' | 'kind'>>
  | Partial<Omit<SequencerItem, 'id' | 'kind'>>
  | Partial<Omit<StatusLedItem, 'id' | 'kind'>>
  | Partial<Omit<FaderItem, 'id' | 'kind'>>
  | Partial<Omit<XYPadItem, 'id' | 'kind'>>
  | Partial<Omit<TimelineLauncherItem, 'id' | 'kind'>>
  | Partial<Omit<PianoItem, 'id' | 'kind'>>
  | Partial<Omit<TransposeItem, 'id' | 'kind'>>
  | Partial<Omit<CapoItem, 'id' | 'kind'>>
  | Partial<Omit<RecQuantItem, 'id' | 'kind'>>
  | Partial<Omit<AbletonRowItem, 'id' | 'kind'>>
  | Partial<Omit<SceneCopyItem, 'id' | 'kind'>>
  | Partial<Omit<WavesAutotuneItem, 'id' | 'kind'>>
  | Partial<Omit<MixerItem, 'id' | 'kind'>>
  | Partial<Omit<RadialItem, 'id' | 'kind'>>
  | Partial<Omit<LabelItem, 'id' | 'kind'>>
  | Partial<Omit<PresetChangeItem, 'id' | 'kind'>>
  | Partial<Omit<SnapshotChangeItem, 'id' | 'kind'>>
  | Partial<Omit<DisplayItem, 'id' | 'kind'>>
  | Partial<Omit<RigStateButtonItem, 'id' | 'kind'>>
  | Partial<Omit<LightColorItem, 'id' | 'kind'>>
  | Partial<Omit<LightLevelItem, 'id' | 'kind'>>
  | Partial<Omit<LightFxItem, 'id' | 'kind'>>
  | Partial<Omit<BlackoutItem, 'id' | 'kind'>>
  | Partial<Omit<OpVisualTriggerItem, 'id' | 'kind'>>
  | Partial<Omit<OpVisualGridItem, 'id' | 'kind'>>
  | Partial<Omit<AudioPadsItem, 'id' | 'kind'>>;

export function editItem(id: string, patch: ItemPatch): void {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return;
  // Touching any MIDI field means the operator has answered the question a
  // pending allocation warning was asking, so the warning stops. Restricted to
  // MIDI fields (not "any edit") so renaming or recolouring a flagged item
  // doesn't silently retire a warning it hasn't addressed.
  if (patchTouchesMidi(patch)) clearMidiAllocFailure(id);
  const items = pg.items.map((it) =>
    it.id === id ? ({ ...it, ...patch } as LayoutItem) : it,
  );
  // Bump nextCC when a button's CC changes (the existing rolling-CC heuristic).
  // ButtonGrid baseCC changes also advance nextCC past the grid's last cell so
  // newly added items don't collide with it. Read via a common-field view —
  // narrower kinds may not carry these but the runtime check is safe.
  const probe = patch as Record<string, unknown>;
  let nextCCval = p.nextCC;
  if (typeof probe.ccNumber === 'number') nextCCval = clampCC(probe.ccNumber + 1);
  if (typeof probe.baseCC === 'number' && typeof probe.cols === 'number' && typeof probe.rows === 'number') {
    nextCCval = clampCC(probe.baseCC + probe.rows * probe.cols);
  } else if (typeof probe.baseCC === 'number') {
    // No rows/cols in this patch — find the existing item to get them.
    const item = pg.items.find((it) => it.id === id);
    if (item && item.kind === 'buttongrid') {
      nextCCval = clampCC(probe.baseCC + item.rows * item.cols);
    }
  }
  setActiveItemsAndNextCC(items, nextCCval);
  // A matrix claim this item owns follows its new binding / name (S317).
  const edited = items.find((it) => it.id === id);
  if (edited) followItemClaimOf(edited);
}

/**
 * Update an item's face name. Empty string = clear the override (omits
 * the `name` field entirely so `exactOptionalPropertyTypes` stays happy
 * and the face falls back to its auto-derived label).
 */
export function setItemName(id: string, name: string): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id) return it;
    if (name === '') {
      const { name: _drop, ...rest } = it;
      void _drop;
      return rest as LayoutItem;
    }
    return { ...it, name } as LayoutItem;
  });
  setActiveItems(items);
  const renamed = items.find((it) => it.id === id);
  if (renamed) followItemClaimOf(renamed);
}

/**
 * Toggle the hideName / hideLabel face flags. Setting back to "visible"
 * deletes the field entirely (default-visible state); setting to hidden
 * writes `true`. Keeps saved layouts terse and avoids
 * `exactOptionalPropertyTypes` friction.
 */
export function setFaceFlag(
  id: string,
  flag: 'hideName' | 'hideLabel',
  hide: boolean,
): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id) return it;
    if (hide) return { ...it, [flag]: true } as LayoutItem;
    const { [flag]: _drop, ...rest } = it;
    void _drop;
    return rest as LayoutItem;
  });
  setActiveItems(items);
}

/**
 * Whole-item dimensions of a PER-CELL kind at a size preset, or null for any
 * other kind. These kinds' SIZE_PRESETS hold ONE cell (or one mixer strip);
 * the item is a count of them, laid out per kind. One function, read by both
 * the S/M/L preset and the resize clamps, so the two can never disagree about
 * what "S" and "L" mean for a row.
 */
export function perCellDims(it: LayoutItem, size: SizePreset): { w: number; h: number } | null {
  switch (it.kind) {
    case 'buttongrid': {
      const cell = sizeFor('buttongrid', size);
      return { w: it.cols * cell.w, h: it.rows * cell.h };
    }
    case 'transpose':
    case 'capo': {
      // One row of semitone cells, end - start + 1 of them.
      const cell = sizeFor(it.kind, size);
      return { w: (it.end - it.start + 1) * cell.w, h: cell.h };
    }
    case 'recQuant':
      return recQuantDims(it.labels.length, it.quantizer, sizeFor('recQuant', size));
    case 'abletonRow': {
      // One row of cells, like Rec Quantize without its Amount cell.
      const cell = sizeFor('abletonRow', size);
      return { w: Math.max(1, it.cells.length) * cell.w, h: cell.h };
    }
    case 'sceneCopy':
      // Stacks the opposite way from transpose/capo: THREE columns (COPY,
      // PASTE, CLEAR) wide, `count` rows tall. Was two before device v0.4
      // added clear_scene; migrateV4toV5 re-widens items persisted then.
      return sceneCopyDims(it.count, it.showDeleteAll, sizeFor('sceneCopy', size));
    case 'wavesAutotune':
      return wavesAutotuneDims(it.scalePlacement ?? 'bottom', it.start, it.end, sizeFor('wavesAutotune', size));
    case 'mixer':
      return mixerDims(effectiveMixerStrips(it), sizeFor('mixer', size));
    case 'audioPads': {
      const cell = sizeFor('audioPads', size);
      return { w: it.cols * cell.w, h: it.rows * cell.h };
    }
    default:
      return null;
  }
}

/**
 * Min/max w and h a freeform resize (edge drag or the Inspector's Width and
 * Height boxes) may give this item.
 *
 * Per-cell kinds are bounded by the WHOLE item at the S and L cell sizes.
 * `clampsFor(kind)` alone bounded them by one cell, so widening a 13-cell
 * Transpose row collapsed it to 84px with 4px cells (operator report
 * 2026-10-10, reproduced by `_probe_transpose_resize`).
 */
export function itemResizeClamps(it: LayoutItem): { minW: number; minH: number; maxW: number; maxH: number } {
  if (it.kind === 'button') {
    const { minW, minH, maxW, maxH } = buttonClampsFor(it.shape ?? 'rect');
    return { minW, minH, maxW, maxH };
  }
  const s = perCellDims(it, 'S');
  const l = perCellDims(it, 'L');
  if (s && l) return { minW: s.w, minH: s.h, maxW: l.w, maxH: l.h };
  return clampsFor(it.kind);
}

/**
 * Apply a size preset (S/M/L). Recomputes w/h from SIZE_PRESETS for the
 * item's kind. ButtonGrid's preset controls per-cell pixels, so its w/h
 * derives from rows × cols × cell-size.
 */
export function setItemSize(id: string, size: SizePreset): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds();
  /** Clamp x/y so the resized item fits inside the canvas region. Same
   *  rule as resizeButtonGrid — growing S→L on an item near an edge
   *  used to bleed out of bounds. */
  function clampPos(x: number, y: number, w: number, h: number): { x: number; y: number } {
    if (!bounds) return { x, y };
    return {
      x: Math.max(0, Math.min(bounds.w - w, x)),
      y: Math.max(0, Math.min(bounds.h - h, y)),
    };
  }
  const items = pg.items.map((it) => {
    if (it.id !== id) return it;
    // Per-cell kinds (grid, the semitone rows, Rec Quant, Scene Copy, Waves,
    // mixer): the preset sizes one cell and the item is a count of them.
    const perCell = perCellDims(it, size);
    if (perCell) {
      const { x, y } = clampPos(it.x, it.y, perCell.w, perCell.h);
      return { ...it, size, x, y, w: perCell.w, h: perCell.h } as LayoutItem;
    }
    if (it.kind === 'button') {
      const dims = buttonSizeFor(it.shape ?? 'rect', size);
      const { x, y } = clampPos(it.x, it.y, dims.w, dims.h);
      return { ...it, size, x, y, w: dims.w, h: dims.h } satisfies ButtonItem;
    }
    if (it.kind === 'statusLed') {
      const dims = sizeFor('statusLed', size);
      const { x, y } = clampPos(it.x, it.y, dims.w, dims.h);
      return { ...it, size, x, y, w: dims.w, h: dims.h } satisfies StatusLedItem;
    }
    if (it.kind === 'opvisualGrid') {
      // The only kind whose presets can exceed the canvas (L is for big
      // screens), so it is capped to the measured region rather than only
      // repositioned - a grid wider than the iPad frame has columns the
      // operator cannot reach.
      const dims = sizeFor('opvisualGrid', size);
      const w = bounds ? Math.min(dims.w, bounds.w) : dims.w;
      const h = bounds ? Math.min(dims.h, bounds.h) : dims.h;
      const { x, y } = clampPos(it.x, it.y, w, h);
      return { ...it, size, x, y, w, h } satisfies OpVisualGridItem;
    }
    const dims = sizeFor(it.kind, size);
    const { x, y } = clampPos(it.x, it.y, dims.w, dims.h);
    return { ...it, size, x, y, w: dims.w, h: dims.h } as LayoutItem;
  });
  setActiveItems(items);
}

/**
 * Change a button's shape. Reflows w/h to the new shape's S/M/L preset
 * (using the button's current size preset), then applies the new shape's
 * aspect rule so any residual mismatch (e.g. flipping from `bar` to
 * `circle` while the button was previously stretched wide) resolves
 * cleanly. Preserves position; caller can move it after.
 */
export function setButtonShape(id: string, shape: ButtonShape): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'button') return it;
    const size: SizePreset = it.size ?? 'M';
    const dims = buttonSizeFor(shape, size);
    // Apply the target shape's aspect rule to the freshly-picked preset
    // dims. The preset itself already satisfies each shape's rule, so
    // this is a no-op today — but keeps the invariant honest if presets
    // ever drift out of sync with the aspect rule.
    const adjusted = applyAspectRule(shape, dims.w, dims.h, 'w');
    return {
      ...it,
      shape,
      w: adjusted.w,
      h: adjusted.h,
    } satisfies ButtonItem;
  });
  setActiveItems(items);
}

/** Cell-scoped editor — replaces or removes a per-cell override. Used by
 *  the long-press cell editor. Passing `undefined` for a field removes that
 *  override so the cell falls back to the grid's default. */
export function editGridCell(
  gridId: string,
  cellId: string,
  patch: Partial<Omit<ButtonGridCell, 'id'>>,
): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== gridId || it.kind !== 'buttongrid') return it;
    return {
      ...it,
      cells: it.cells.map((c) => (c.id === cellId ? { ...c, ...patch } : c)),
    };
  });
  setActiveItems(items);
}

/** The Inspector's Colour and Font rows on a grid set the WHOLE grid
 *  (operator, 2026-10-15: both "doesn't seem to make a difference" - the
 *  grid read only each cell's own settings - and "my grids are usually
 *  similarly coloured within the group"). A colour clears every cell's own
 *  so all of them take the grid's; no colour puts the rainbow back. A font
 *  clears every cell's own size. A cell's row can still override after. */
export function setGridLook(gridId: string, look: { hue?: PaletteHue; nameSize?: NameSize }): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== gridId || it.kind !== 'buttongrid') return it;
    const cells = it.cells.map((c, i) => {
      const next: ButtonGridCell = { ...c };
      if (look.hue === 'none') next.color = CELL_COLOR_PALETTE[i % CELL_COLOR_PALETTE.length] ?? 'cyan';
      else if (look.hue) delete next.color;
      if (look.nameSize) delete next.nameSize;
      return next;
    });
    return { ...it, ...look, cells } satisfies ButtonGridItem;
  });
  setActiveItems(items);
}

export function addButton(originId?: string): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is ButtonItem => i.id === originId && i.kind === 'button')
    : undefined;
  // The template is the first MIDI button on the page (2026-10-05). OBS
  // Scene, Ableton Control, Ableton Device, Light Preset and Run Program are
  // buttons underneath, and templating off one made the "Button" tile spawn
  // an OBS scene button on a page that began with one (smoke
  // new_button_is_a_midi_button).
  const template = origin ?? pg.items.find((i): i is ButtonItem => i.kind === 'button' && buttonEmitsMidi(i.actionType));

  const cc = p.nextCC;
  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    ccNumber: cc,
    channel: template?.channel ?? 0,
    onValue: template?.onValue ?? 127,
    color: template?.color ?? 'cyan',
    // hue: 'none' explicitly, so a NEW item is born UNCOLOURED and the
    // palette's no-colour chip is actually the default (2026-07-30).
    // `hueOf()` prefers `hue` over the legacy `color`, so this neutralises
    // the seed below without touching it - and the seed has to stay,
    // because `color` is a REQUIRED field on sixteen item interfaces and
    // pre-DEC-127 projects still read their colour through it.
    // Before this, every kind spawned pre-coloured (Stream Deck and
    // Transpose yellow, XY/LFO/Waves/Macro magenta, and so on), so `none`
    // was unreachable for anything freshly added.
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    // A fresh button (no template on the page yet) defaults to Note +
    // Momentary (operator ask, 2026-08-23) - a footswitch-style tap-and-hold
    // is the more common first reach than a latching CC toggle. Templating
    // off an existing button (as every other field here does) still wins,
    // so a page already full of CC/toggle buttons keeps spawning more of
    // the same rather than forcing a mode-switch on every new tile.
    actionType: template?.actionType ?? 'noteon',
    mode: template?.mode ?? 'momentary',
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(cc + 1));
  return item;
}

/**
 * Spawn an Ableton Control button - a normal Button with `actionType:
 * 'ableton'` and one step already on it.
 *
 * Deliberately NOT a new item kind. Everything a Button already has comes
 * free this way: all twelve shapes, all seven skins, sizing, the colour
 * palette, rotation, multi-select, undo/redo, MIDI-in triggers, Stream Deck,
 * Rig State capture, and - the one that matters most - Timeline `buttonRef`
 * steps, which is what makes rec quant / launch quant / metronome
 * automatable on a timeline without inventing a timeline-side mechanism.
 *
 * It spawns with a tempo step rather than an empty list because an empty
 * control that does nothing until you find the Inspector fails the
 * "i wanna do this and it's doable immediately" bar. Tempo is the most
 * common reach and the step is immediately editable.
 *
 * No CC is claimed (`setActiveItems`, not `setActiveItemsAndNextCC`): an
 * Ableton Control button emits no MIDI at all, so taking a CC slot would
 * quietly exhaust the rig's allocation for nothing and show up in the
 * layout linter's duplicate check as a phantom.
 *
 * `step` (DEC-291): the palette's "Ableton Control > <control> > One button"
 * passes the step it picked, and it wins over a template's steps - the
 * operator just said what this button is for. Without one it templates the
 * steps as before.
 */
export function addAbletonControl(originId?: string, step?: AbletonStep): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is ButtonItem => i.id === originId && i.kind === 'button')
    : undefined;
  // Template off an existing ABLETON button only - templating off any button
  // would inherit a MIDI button's channel/CC into a control that has no use
  // for them, and (worse) copy its shape/size from an unrelated control.
  const template = origin ?? pg.items.find(
    (i): i is ButtonItem => i.kind === 'button' && i.actionType === 'ableton',
  );

  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    // Required by the interface but meaningless here - nothing reads them
    // while actionType is 'ableton'. Kept so switching action type stays
    // non-destructive, same contract the MIDI fields already have.
    ccNumber: 0,
    channel: template?.channel ?? 0,
    onValue: 127,
    color: template?.color ?? 'cyan',
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    actionType: 'ableton',
    // Trigger, not toggle: setting a tempo has no OFF counterpart, the same
    // reason a `pc` button always behaves as a trigger.
    mode: 'trigger',
    abletonSteps: step
      ? [structuredClone(step)]
      : template?.abletonSteps
      ? structuredClone(template.abletonSteps)
      : [{
          id: `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
          type: 'ableton.tempo',
          target: '',
          bpm: 120,
          clock: 'ableton',
        }],
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn an OBS Scene button - a normal Button with `actionType: 'obs'`.
 *
 * Deliberately NOT a new item kind, for exactly the reasons DEC-180 locked
 * for Ableton Control: every shape, skin, size, colour, rotation, guard,
 * multi-select, undo/redo, MIDI-in trigger, Stream Deck binding and - the
 * one that matters most - Timeline `buttonRef` step comes free from Button,
 * and would each have to be rebuilt for a bespoke kind. A scene switch is
 * the same SHAPE of thing an Ableton Control button is: one tap, one named
 * command, no MIDI, no OFF.
 *
 * It spawns with NO scene chosen, which is the one place it departs from
 * `addAbletonControl` (which spawns with a tempo step so the control does
 * something immediately). There is no sensible default scene: the names are
 * the operator's own, they live on another machine, and guessing one would
 * mean a button that confidently switches to the wrong thing. Instead the
 * unconfigured state is loud on the face ("pick a scene") and the Inspector
 * opens on a live list pulled from the bridge, so the "i wanna do this"
 * path is still one tap plus one pick.
 *
 * Templates off an existing OBS button only, so a second scene button
 * inherits the first one's shape, size and - critically - its `bridgeIds`.
 * The bridge is the hard part of configuring this control (OBS runs on the
 * streaming PC, not the default bridge), so having to set it once per
 * layout rather than once per button is most of the ergonomics.
 *
 * No CC is claimed (`setActiveItems`, not `setActiveItemsAndNextCC`): this
 * button emits no MIDI, so taking a slot would exhaust the rig's allocation
 * for nothing. `performance/lint.ts::ccClaims` skips it for the same reason.
 */
export function addObsControl(originId?: string): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is ButtonItem => i.id === originId && i.kind === 'button')
    : undefined;
  const template = origin ?? pg.items.find(
    (i): i is ButtonItem => i.kind === 'button' && i.actionType === 'obs',
  );

  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    ccNumber: 0,
    channel: template?.channel ?? 0,
    onValue: 127,
    color: template?.color ?? 'magenta',
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    actionType: 'obs',
    mode: 'trigger',
    obsScene: '',
    // AIM IT AT A MACHINE AT SPAWN, so the common rig needs no machine
    // configuration at all. The operator's framing (2026-09-19) is that
    // there is generally ONE OBS on the LAN, and a control that demands a
    // machine when only one answer is possible fails the "i wanna do this"
    // bar on its very first step. `autoPickObsBridge` picks the sole
    // OBS-capable bridge, or the sole bridge on a single-bridge rig, and
    // returns null whenever there is a genuine choice to make - that is the
    // case the Computer dropdown exists for, and guessing it would be the
    // silent misroute DEC-205 refuses.
    //
    // A template wins over the auto-pick: a second scene button on a page
    // should inherit the machine the first one is aimed at, even if that is
    // not the one discovery would have chosen.
    ...(template?.bridgeIds
      ? { bridgeIds: [...template.bridgeIds] }
      : (() => {
          const pick = autoPickObsBridge(bridges().map((b) => b.id));
          return pick ? { bridgeIds: [pick] } : {};
        })()),
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn a Run Program button - a normal Button with
 * `actionType: 'runProgram'` that starts one program registered on one
 * machine's OPBridge.
 *
 * Same shape as `addObsControl`, for the same reasons: it spawns with NO
 * program chosen (the list lives on the target machine and guessing one
 * would be a button that confidently starts the wrong thing), templates off
 * an existing program button so a second one inherits its machine, claims
 * no CC (it emits no MIDI), and auto-aims at the only sensible machine.
 *
 * Auto-aiming is safe here for OBS's reason: a wrong guess SHOWS. The far
 * end reads its own list back and the face says "not on that computer".
 */
export function addProgramControl(originId?: string): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is ButtonItem => i.id === originId && i.kind === 'button')
    : undefined;
  const template = origin ?? pg.items.find(
    (i): i is ButtonItem => i.kind === 'button' && i.actionType === 'runProgram',
  );

  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    ccNumber: 0,
    channel: template?.channel ?? 0,
    onValue: 127,
    color: template?.color ?? 'green',
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    actionType: 'runProgram',
    mode: 'trigger',
    runProgramId: '',
    ...(template?.bridgeIds
      ? { bridgeIds: [...template.bridgeIds] }
      : (() => {
          const pick = autoPickProgramBridge(bridges().map((b) => b.id));
          return pick ? { bridgeIds: [pick] } : {};
        })()),
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn a Light Preset button - a normal Button with
 * `actionType: 'lightPreset'` that plays one of OPBridge's built-in lighting
 * presets on the lights of one machine.
 *
 * Spawns with NO preset chosen, for Run Program's reason: the list lives on
 * the target machine, and a guessed preset (a strobe, say) is worse than a
 * button that says "pick a preset". Mode starts as Toggle - press to play,
 * press again to stop - because most presets run until stopped, and a
 * one-shot that starts an endless chase has no way to end it from itself.
 *
 * Aims itself at the machine that OUTPUTS the rig's lights when that is one
 * paired machine (see `autoPickPresetBridge`), else the only paired machine.
 */
export function addLightPresetControl(originId?: string): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is ButtonItem => i.id === originId && i.kind === 'button')
    : undefined;
  const template = origin ?? pg.items.find(
    (i): i is ButtonItem => i.kind === 'button' && i.actionType === 'lightPreset',
  );

  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    ccNumber: 0,
    channel: template?.channel ?? 0,
    onValue: 127,
    color: template?.color ?? 'magenta',
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    actionType: 'lightPreset',
    mode: template?.mode ?? 'toggle',
    lightPreset: { presetId: '' },
    ...(template?.bridgeIds
      ? { bridgeIds: [...template.bridgeIds] }
      : (() => {
          const pick = autoPickPresetBridge(bridges());
          return pick ? { bridgeIds: [pick] } : {};
        })()),
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn a Hue Scene button (2026-10-23, OPBridge S331): `actionType: 'hueScene'`, recalling one of the Hue app's own
 * scenes through OPBridge (`lighting/hueScenes.ts`). Unconfigured until a scene is picked in the Inspector, which also
 * names it. A one-shot (trigger); it lights while its scene is the room's active one. Aimed at the computer that lists
 * the Hue scenes when one already has, else left for the Inspector's pick to aim.
 */
export function addHueSceneControl(): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const template = pg.items.find((i): i is ButtonItem => i.kind === 'button' && i.actionType === 'hueScene');
  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);
  const home = hueSceneEntries()[0]?.rowId;
  const homeNode = home ? (bridges().find((b) => b.id === home)?.nodeId ?? home) : undefined;
  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    ccNumber: 0,
    channel: 0,
    onValue: 127,
    color: template?.color ?? 'magenta',
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    actionType: 'hueScene',
    mode: 'trigger',
    hueScene: { bridge: '', scene: '' },
    ...(homeNode ? { bridgeIds: [homeNode] } : {}),
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn an Ableton Device toggle.
 *
 * Templates off an existing device button so a second one on a page inherits
 * the machine and the TRACK - two effects on the same vocal track is the
 * common case, and retyping the track name each time fails the "i wanna do
 * this" bar. The DEVICE name is deliberately NOT inherited: two buttons
 * pointing at the same device would be a duplicate, not a convenience.
 *
 * No CC is claimed (`setActiveItems`, not `setActiveItemsAndNextCC`) for the
 * same reason as `addObsControl`: this button emits no MIDI, so taking a CC
 * slot would exhaust the rig's allocation for nothing.
 *
 * ⚠️ NO auto-pick of the bridge, unlike OBS - and the asymmetry is
 * deliberate. `autoPickObsBridge` can guess because a wrong guess SHOWS: the
 * far end answers 503 and the button reads "OBS not reachable". Here the
 * address is loopback, so a wrong guess is accepted and silently dropped
 * with no error anywhere in the stack. There is no failure signal to correct
 * from, so the operator picks the machine once, on purpose. The `no-bridge`
 * rung of the fault ladder is what makes that ask visible rather than
 * mysterious.
 */
export function addAbletonDevice(originId?: string): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is ButtonItem => i.id === originId && i.kind === 'button')
    : undefined;
  const template = origin ?? pg.items.find(
    (i): i is ButtonItem => i.kind === 'button' && i.actionType === 'abletonDevice',
  );

  const size = template?.size ?? DEFAULT_SIZE;
  const shape = template?.shape ?? 'rect';
  const dims = buttonSizeFor(shape, size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w, h,
    size,
    ccNumber: 0,
    channel: template?.channel ?? 0,
    onValue: 127,
    color: template?.color ?? 'cyan',
    hue: 'none',
    shape,
    rotation: template?.rotation ?? 0,
    actionType: 'abletonDevice',
    // Trigger-shaped, like the metronome: it HAS an inverse, but the value
    // is resolved against readback at dispatch, never latched here.
    mode: 'trigger',
    abletonDevice: {
      trackName: template?.abletonDevice?.trackName ?? '',
      deviceName: '',
    },
    ...(template?.bridgeIds ? { bridgeIds: [...template.bridgeIds] } : {}),
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn a Fader. Templates off any existing fader for CC/channel/color/
 * orientation continuity. Fresh spawn takes `p.nextCC` (auto-incremented)
 * and defaults to vertical with linear response, resting at 0.
 */
export function addFader(originId?: string): FaderItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `fdr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is FaderItem => i.id === originId && i.kind === 'fader')
    : undefined;
  const template = origin ?? pg.items.find((i): i is FaderItem => i.kind === 'fader');

  const cc = p.nextCC;
  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('fader', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: FaderItem = {
    id,
    kind: 'fader',
    x, y, w, h,
    size,
    ccNumber: cc,
    channel: template?.channel ?? 0,
    value: template?.centered ? 64 : 0,
    color: template?.color ?? 'cyan',
    hue: 'none',
    centered: template?.centered ?? false,
    response: template?.response ?? 'linear',
    responseFactor: template?.responseFactor ?? 100,
    vertical: template?.vertical ?? true,
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(cc + 1));
  return item;
}

/**
 * Spawn a Radial knob (DEC-116). Templates off any existing radial for
 * channel/color/size continuity, exactly like addFader. Allocates one CC
 * from the rolling counter. Square by construction (`isSquareKind`).
 */
export function addRadial(originId?: string): RadialItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `rad-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is RadialItem => i.id === originId && i.kind === 'radial')
    : undefined;
  const template = origin ?? pg.items.find((i): i is RadialItem => i.kind === 'radial');

  const cc = p.nextCC;
  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('radial', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: RadialItem = {
    id,
    kind: 'radial',
    x, y, w, h,
    size,
    ccNumber: cc,
    channel: template?.channel ?? 0,
    value: template?.defaultValue ?? 0,
    color: template?.color ?? 'cyan',
    hue: 'none',
    defaultValue: template?.defaultValue ?? 0,
    sweepDeg: template?.sweepDeg ?? 270,
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(cc + 1));
  return item;
}

/**
 * Spawn a `presetChange` tile bound to a SPECIFIC registered pedal (DEC-152).
 * The palette's "Guitar FX Preset" tile is disabled whenever `activePedals()`
 * is empty (2026-08-04 — the operator's own words: "grey out... add a guitar
 * pedal in Connected Devices to activate this item"), so this is the ONLY
 * way a `presetChange` item is created — there is no unconfigured/portless
 * fallback to template off anymore. Size/color still template off an
 * existing tile for visual continuity.
 */
export function addPresetChangeForPedal(pedal: GuitarPedal): PresetChangeItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `pc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const template = pg.items.find((i): i is PresetChangeItem => i.kind === 'presetChange');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('presetChange', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: PresetChangeItem = {
    id,
    kind: 'presetChange',
    x, y, w, h,
    size,
    device: 'helix',
    pedalId: pedal.id,
    setlist: 0,
    program: 0,
    snapshot: undefined,
    color: template?.color ?? 'amber',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn a `pc`-action Button bound to a registered CUSTOM pedal (DEC-152),
 * from the palette's "Guitar FX Preset" pick-your-pedal flow.
 *
 * A Custom pedal never gets its own item kind — that's reserved for devices
 * with a Helix-style multi-message compound protocol. It reuses the
 * already-shipped Button + `pc` action type (CP-075), pre-filled with the
 * pedal's own port/channel so the tile reaches the pedal instead of
 * Ableton's default port. `name` is the operator-typed preset name (stored
 * on `GuitarPedal.presetNames`, mirroring `helixSetlistOverride`), carried
 * onto the item's face the same way `PresetChangeItem.presetName` is.
 */
export function addGuitarFxButton(pedal: GuitarPedal, program: number, name: string): ButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `btn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const shape: ButtonShape = 'rect';
  const dims = buttonSizeFor(shape, DEFAULT_SIZE);
  const { x, y } = findFreeSpot(dims.w, dims.h);
  const cc = p.nextCC;

  const item: ButtonItem = {
    id,
    kind: 'button',
    x, y, w: dims.w, h: dims.h,
    size: DEFAULT_SIZE,
    ccNumber: cc,
    // Vestigial once pedalId is set (FireButton resolves the live pedal's
    // channel instead) — still written so the field type stays required for
    // every other Button use, and so the item is legible in isolation.
    channel: pedal.channel,
    onValue: 127,
    color: 'amber',
    hue: 'none',
    shape,
    rotation: 0,
    actionType: 'pc',
    program,
    pedalId: pedal.id,
    name,
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(cc + 1));
  return item;
}

/**
 * Spawn a snapshot tile (DEC-151). Templates off an existing one for
 * pedalId/colour/size, and — unlike addPresetChangeForPedal, which
 * deliberately resets `program` — **advances the snapshot number**.
 *
 * That difference is the whole authoring story. A bank of eight snapshots is
 * the canonical layout (it mirrors the pedal's own footswitch row), so
 * spawning eight in a row should mean eight taps on the palette and zero
 * trips to the Inspector. Two preset tiles pointing at the same preset is a
 * mistake; eight snapshot tiles walking 1→8 is the point. Wraps at 8 so a
 * ninth tap starts the row again rather than sending an out-of-range value.
 *
 * Claims no CC from the rolling counter: CC69 is a FIXED, device-defined
 * number on the pedal's own channel, not rig-allocatable space. Same
 * reasoning as presetChange and the transpose/waves items.
 */
export function addSnapshotChange(originId?: string): SnapshotChangeItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `sn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is SnapshotChangeItem => i.id === originId && i.kind === 'snapshotChange')
    : undefined;
  // Falls back to the LAST snapshot tile on the page, not the first: when
  // laying out a row left-to-right the one just placed is the one to walk on
  // from. (presetChange templates off the first because it inherits settings
  // rather than continuing a sequence.)
  const template = origin
    ?? [...pg.items].reverse().find((i): i is SnapshotChangeItem => i.kind === 'snapshotChange');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('snapshotChange', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  // A relative template ('next'/'prev') has no number to advance from, so the
  // new tile starts the absolute row at 1 rather than guessing.
  const prev = template?.snapshot;
  const nextSnapshot = typeof prev === 'number' ? (prev + 1) % 8 : 0;

  // Inherited, not defaulted to a guess: which pedal is the one thing the
  // author cannot discover from the item itself. Also inherited from a
  // presetChange tile if one exists — the pedal is the same pedal, and
  // making the author re-pick per item kind would be exactly the friction
  // this item is here to remove. With neither, fall back to the first
  // registered Helix pedal (a "Helix Snapshot" tile is Helix-only by
  // definition) — absent even that, `pedalId` stays unset and the tile
  // reports the same dead state the palette tile itself gates on.
  const pedalId = template?.pedalId
    ?? pg.items.find((i): i is PresetChangeItem => i.kind === 'presetChange')?.pedalId
    ?? activePedals().find((pd) => pd.type === 'helix')?.id;

  const item: SnapshotChangeItem = {
    id,
    kind: 'snapshotChange',
    x, y, w, h,
    size,
    device: template?.device ?? 'helix',
    pedalId,
    snapshot: nextSnapshot,
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Rig State button (PLAN.md QUEUED "Rig States") - recalls ONE look from a
 * bank. `bankId` inherits from the last placed rigStateButton on the page
 * (same "continuing a sequence" reasoning as snapshotChange's pedal
 * inherit) - placing several buttons for looks in the same bank is the
 * common flow. `lookId` deliberately does NOT inherit: a fresh copy of the
 * same look would be a pointless duplicate, so it starts unset and the
 * author picks it in the Inspector.
 */
// ============================================================
// Lighting spawners (PLAN.md QUEUED "DMX / lighting v1")
// ============================================================

/**
 * TEMPLATING RULE FOR THE LIGHTING SET, and why it differs per field.
 *
 * Every spawner here copies `target` from the last lighting item placed on
 * the page, and none of them copies the thing that makes the control DO
 * something (the colour, the effect). That split is not arbitrary: placing a
 * row of colour buttons aimed at the same lights is the common flow, and a
 * second button that is an exact duplicate of the first is never what anyone
 * wanted. Same reasoning `addRigStateButton` uses for bank-inherits-but-look-
 * does-not.
 *
 * The template is read across ALL FOUR lighting kinds rather than per-kind,
 * because "point these at the wash group" is a thought the operator has once
 * per page, not once per kind.
 */
function lastLightingTarget(pg: Page): DmxTarget | undefined {
  const prev = [...pg.items].reverse().find(
    (i): i is LightColorItem | LightLevelItem | LightFxItem =>
      i.kind === 'lightColor' || i.kind === 'lightLevel' || i.kind === 'lightFx',
  );
  return prev?.target;
}

/**
 * Clear a Light Colour's optional `level`, so the button sends a colour and
 * leaves brightness alone.
 *
 * Needs its own destructure-and-replace rather than `editItem({ level:
 * undefined })`: `editItem`'s plain `{...it, ...patch}` merge can only
 * OVERWRITE a key, never DELETE one, and `exactOptionalPropertyTypes` forbids
 * assigning `undefined` to an optional field anyway. Patching it to
 * `undefined` would leave the key present and `'level' in item` true - which
 * `JSON.stringify` happens to hide today and which breaks the moment anything
 * iterates keys. Same reasoning as `setRigStateButtonLook`.
 */
export function clearLightLevelField(id: string): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'lightColor') return it;
    const { level: _drop, ...rest } = it;
    void _drop;
    return rest;
  });
  setActiveItems(items);
}

/** Light Colour - set a stage colour on the target. */
export function addLightColor(originId?: string): LightColorItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const origin = originId
    ? pg.items.find((i): i is LightColorItem => i.id === originId && i.kind === 'lightColor')
    : undefined;
  const template = origin
    ?? [...pg.items].reverse().find((i): i is LightColorItem => i.kind === 'lightColor');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('lightColor', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: LightColorItem = {
    id: `lc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'lightColor',
    x, y, w, h,
    size,
    target: template?.target ?? lastLightingTarget(pg) ?? DEFAULT_DMX_TARGET,
    // Colour deliberately does NOT template: a second button that is an exact
    // duplicate of the first is a duplicate, not a continuation. It starts on
    // White so a freshly-placed button does something visible on first tap.
    color_label: DEFAULT_STAGE_COLOR,
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/** Light Level - a fader on the target's master level. */
export function addLightLevel(originId?: string): LightLevelItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const origin = originId
    ? pg.items.find((i): i is LightLevelItem => i.id === originId && i.kind === 'lightLevel')
    : undefined;
  const template = origin
    ?? [...pg.items].reverse().find((i): i is LightLevelItem => i.kind === 'lightLevel');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('lightLevel', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: LightLevelItem = {
    id: `ll-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'lightLevel',
    x, y, w, h,
    size,
    target: template?.target ?? lastLightingTarget(pg) ?? DEFAULT_DMX_TARGET,
    // Starts at FULL, not zero. A fader that starts at the bottom looks
    // identical to a fader that is broken, and the first thing anyone does
    // with a new level fader is pull it DOWN from where the lights are.
    value: 255,
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/** Light FX - arm a named effect on the target. */
export function addLightFx(originId?: string): LightFxItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const origin = originId
    ? pg.items.find((i): i is LightFxItem => i.id === originId && i.kind === 'lightFx')
    : undefined;
  const template = origin
    ?? [...pg.items].reverse().find((i): i is LightFxItem => i.kind === 'lightFx');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('lightFx', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: LightFxItem = {
    id: `lfx-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'lightFx',
    x, y, w, h,
    size,
    target: template?.target ?? lastLightingTarget(pg) ?? DEFAULT_DMX_TARGET,
    // Empty is MEANINGFUL, not unset: OPBridge reads an empty effect as STOP.
    // So a freshly-placed FX button is a working "stop the effects" button
    // until the author picks one, rather than a dead tile. The face says so.
    effect: '',
    color: template?.color ?? 'magenta',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/** Blackout - kill the rig. No target by design. */
export function addBlackout(originId?: string): BlackoutItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const origin = originId
    ? pg.items.find((i): i is BlackoutItem => i.id === originId && i.kind === 'blackout')
    : undefined;
  const template = origin
    ?? [...pg.items].reverse().find((i): i is BlackoutItem => i.kind === 'blackout');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('blackout', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: BlackoutItem = {
    id: `blk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'blackout',
    x, y, w, h,
    size,
    // Guard ON by default, the only kind in the app that does this. A
    // rig-wide kill is worth one extra tap in show mode, and the operator
    // reaching for it in a hurry is also the one most likely to brush it.
    // Still editable - this is a default, not a lock.
    guard: template?.guard ?? true,
    color: template?.color ?? 'amber',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

// ============================================================
// OPVisual spawners (.notepads/opvisual-control-plan.md)
// ============================================================

/**
 * OPVisual Trigger - one cell, column or action. Templating continues a row:
 * a second Trigger after one aimed at `L1-3` targets `L1-4`, because placing a
 * row of cells is the common flow and an exact duplicate never is. The
 * machine is auto-picked when unambiguous (`autoPickOpVisualBridge`) so the
 * operator's one-OPVisual rig needs no setup.
 */
export function addOpVisualTrigger(originId?: string): OpVisualTriggerItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is OpVisualTriggerItem => i.id === originId && i.kind === 'opvisualTrigger')
    : undefined;
  const template = origin
    ?? [...pg.items].reverse().find((i): i is OpVisualTriggerItem => i.kind === 'opvisualTrigger');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('opvisualTrigger', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const prev = template?.action;
  const action: OpVisualAction =
    prev?.verb === 'cell' ? { verb: 'cell', layer: prev.layer, col: (prev.col % OPVISUAL_COLS) + 1 }
    : prev?.verb === 'column' ? { verb: 'column', col: (prev.col % OPVISUAL_COLS) + 1 }
    : { verb: 'column', col: 1 };

  const machine = template?.bridgeIds ?? (() => {
    const pick = autoPickOpVisualBridge();
    return pick ? [pick] : undefined;
  })();

  const item: OpVisualTriggerItem = {
    id: `opvt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'opvisualTrigger',
    x, y, w, h,
    size,
    action,
    quant: template?.quant ?? 'bar',
    ...(machine ? { bridgeIds: machine } : {}),
    // The channel follows the machine: a template on OSC means this rig
    // chose OSC for that OPVisual (DEC-215). Absent stays absent = OPBridge.
    ...(template?.sendVia ? { sendVia: template.sendVia } : {}),
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/** Audio pads: one pad (1 x 1) or a grid, empty until a file is assigned (Inspector, or a file dropped on a pad).
 *  The PC and output follow the newest pads item on the page, so a second grid plays where the first one does. */
export function addAudioPads(rows = 1, cols = 1): AudioPadsItem | null {
  const pg = activePage();
  if (!pg) return null;
  const template = [...pg.items].reverse().find((i): i is AudioPadsItem => i.kind === 'audioPads');
  const size: SizePreset = DEFAULT_SIZE;
  const cell = sizeFor('audioPads', size);
  const w = cols * cell.w;
  const h = rows * cell.h;
  const { x, y } = findFreeSpot(w, h);
  const pads: AudioPad[] = Array.from({ length: rows * cols }, (_, i) => ({ color: CELL_COLOR_PALETTE[i % CELL_COLOR_PALETTE.length] ?? 'cyan' }));
  const item: AudioPadsItem = {
    id: `pads-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'audioPads',
    x, y, w, h,
    size,
    rows, cols,
    pads,
    ...(template?.pc ? { pc: template.pc } : {}),
    ...(template?.output ? { output: template.output } : {}),
    color: 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/** OPVisual Grid - the whole surface. Spawns as large as the canvas allows up
 *  to the M preset, because this is the item you give a page to. */
export function addOpVisualGrid(originId?: string): OpVisualGridItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is OpVisualGridItem => i.id === originId && i.kind === 'opvisualGrid')
    : undefined;
  const template = origin ?? pg.items.find((i): i is OpVisualGridItem => i.kind === 'opvisualGrid');

  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('opvisualGrid', size);
  const floor = sizeFor('opvisualGrid', 'S');
  // Measured canvas (header excluded) so the grid fits the visible region.
  const canvas = usableCanvasBounds() ?? activeCanvasSize();
  const w = template?.w ?? Math.max(floor.w, Math.min(dims.w, (canvas?.w ?? dims.w + 80) - 80));
  const h = template?.h ?? Math.max(floor.h, Math.min(dims.h, (canvas?.h ?? dims.h + 80) - 80));
  const { x, y } = findFreeSpot(w, h);

  const machine = template?.bridgeIds ?? (() => {
    const pick = autoPickOpVisualBridge();
    return pick ? [pick] : undefined;
  })();

  const item: OpVisualGridItem = {
    id: `opvg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'opvisualGrid',
    x, y, w, h,
    size,
    quant: template?.quant ?? 'bar',
    cols: template?.cols ?? OPVISUAL_COLS,
    showLayerControls: template?.showLayerControls ?? true,
    showMasterLook: template?.showMasterLook ?? true,
    showViz: template?.showViz ?? true,
    ...(machine ? { bridgeIds: machine } : {}),
    ...(template?.sendVia ? { sendVia: template.sendVia } : {}),
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * (Rig State spawner below.)
 */
export function addRigStateButton(originId?: string): RigStateButtonItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `rsb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is RigStateButtonItem => i.id === originId && i.kind === 'rigStateButton')
    : undefined;
  const template = origin
    ?? [...pg.items].reverse().find((i): i is RigStateButtonItem => i.kind === 'rigStateButton');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('rigStateButton', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: RigStateButtonItem = {
    id,
    kind: 'rigStateButton',
    x, y, w, h,
    size,
    ...(template?.bankId ? { bankId: template.bankId } : {}),
    color: template?.color ?? 'amber',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Change a RigStateButtonItem's bank, clearing `lookId` in the same write -
 * a look id from the OLD bank would silently point at the wrong bank's
 * look array otherwise. `editItem`'s plain `{...it, ...patch}` merge can
 * only OVERWRITE keys a patch supplies, never delete one (same reason
 * `setItemName`'s empty-string clear destructures the field out directly
 * instead of patching it to `undefined` - `exactOptionalPropertyTypes`
 * forbids that anyway), so this needs its own destructure-and-replace
 * rather than going through `editItem`. */
export function setRigStateButtonBank(id: string, bankId: string): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'rigStateButton') return it;
    const { lookId: _drop, ...rest } = it;
    void _drop;
    return { ...rest, bankId };
  });
  setActiveItems(items);
}

/** Set or clear a RigStateButtonItem's look. `lookId: undefined` (the
 *  "- pick a look -" option) destructures the field out entirely rather
 *  than patching it to `undefined` - same `exactOptionalPropertyTypes`
 *  reasoning as `setRigStateButtonBank` above. */
export function setRigStateButtonLook(id: string, lookId: string | undefined): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'rigStateButton') return it;
    if (lookId) return { ...it, lookId };
    const { lookId: _drop, ...rest } = it;
    void _drop;
    return rest;
  });
  setActiveItems(items);
}

/**
 * Spawn a Label (DEC-117). Claims NO CC — it fires nothing — so unlike
 * every other spawner this does not touch the rolling nextCC counter.
 * Templates off an existing label for style continuity only.
 */
export function addLabel(originId?: string): LabelItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `lbl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is LabelItem => i.id === originId && i.kind === 'label')
    : undefined;
  const template = origin ?? pg.items.find((i): i is LabelItem => i.kind === 'label');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('label', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: LabelItem = {
    id,
    kind: 'label',
    x, y, w, h,
    size,
    text: 'Label',
    color: template?.color ?? 'cyan',
    hue: 'none',
    align: template?.align ?? 'center',
    valign: template?.valign ?? 'middle',
    textSize: template?.textSize ?? 'auto',
    background: template?.background ?? false,
    border: template?.border ?? false,
  };
  // No clampCC / nextCC bump: labels claim no CC.
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn a Display. Templates off any existing display for style/color
 * continuity. Defaults `source: 'bpm'` - the one source that needs zero
 * extra Inspector configuration to render something meaningful, so a
 * freshly-spawned Display is immediately useful. Claims no CC (read-only,
 * never sends), same as Label.
 */
export function addDisplay(originId?: string): DisplayItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `dsp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is DisplayItem => i.id === originId && i.kind === 'display')
    : undefined;
  const template = origin ?? pg.items.find((i): i is DisplayItem => i.kind === 'display');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('display', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: DisplayItem = {
    id,
    kind: 'display',
    x, y, w, h,
    size,
    color: template?.color ?? 'cyan',
    hue: 'none',
    style: template?.style ?? 'skin',
    source: template?.source ?? 'bpm',
  };
  // No clampCC / nextCC bump: displays claim no CC.
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Spawn an XY Pad. Templates off any existing pad for channel/color
 * continuity. Fresh spawn allocates TWO CCs (nextCC for X, nextCC+1 for
 * Y) and lands centered (64, 64) — resting position of a Kaoss-style
 * pad. `returnToCenter` defaults to false; user opts into momentary
 * behavior via the Inspector.
 */
export function addXYPad(originId?: string): XYPadItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `xy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is XYPadItem => i.id === originId && i.kind === 'xypad')
    : undefined;
  const template = origin ?? pg.items.find((i): i is XYPadItem => i.kind === 'xypad');

  const xCc = p.nextCC;
  const yCc = clampCC(xCc + 1);
  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('xypad', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: XYPadItem = {
    id,
    kind: 'xypad',
    x, y, w, h,
    size,
    color: template?.color ?? 'magenta',
    hue: 'none',
    xCcNumber: xCc,
    xChannel: template?.xChannel ?? 0,
    yCcNumber: yCc,
    yChannel: template?.yChannel ?? 0,
    xValue: 64,
    yValue: 64,
    returnToCenter: template?.returnToCenter ?? false,
    yInverted: template?.yInverted ?? true,
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(yCc + 1));
  return item;
}

/**
 * Spawn a Timeline Launcher. Fresh spawn takes no CC (fires timeline
 * commands, not MIDI). `timelineId` starts empty — user picks a timeline
 * via the Inspector; item renders a "choose a timeline" placeholder
 * until then. Default mode is `whole+sections`.
 */
export function addTimelineLauncher(originId?: string): TimelineLauncherItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `tl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is TimelineLauncherItem => i.id === originId && i.kind === 'timelineLauncher')
    : undefined;
  const template = origin ?? pg.items.find((i): i is TimelineLauncherItem => i.kind === 'timelineLauncher');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('timelineLauncher', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: TimelineLauncherItem = {
    id,
    kind: 'timelineLauncher',
    x, y, w, h,
    size,
    color: template?.color ?? 'green',
    hue: 'none',
    timelineId: '',
    mode: template?.mode ?? 'whole+sections',
    trigger: template?.trigger ?? 'nextBar',
    loop: template?.loop ?? 'inherit',
  };
  // Don't advance nextCC — Timeline Launcher doesn't own a CC.
  const proj = activeProject();
  if (proj) setActiveItemsAndNextCC([...pg.items, item], proj.nextCC);
  return item;
}

/**
 * Spawn a Piano. Templates off any existing piano for channel/velocity/
 * root-octave/mode continuity. Fresh spawn defaults to 2 octaves rooted
 * at C3 (Ableton middle-C convention), momentary mode, velocity 100.
 * Doesn't consume a CC — pianos send Note On/Off, not CCs.
 */
export function addPiano(originId?: string): PianoItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `pno-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is PianoItem => i.id === originId && i.kind === 'piano')
    : undefined;
  const template = origin ?? pg.items.find((i): i is PianoItem => i.kind === 'piano');

  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('piano', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: PianoItem = {
    id,
    kind: 'piano',
    x, y, w, h,
    size,
    color: template?.color ?? 'cyan',
    hue: 'none',
    octaves: template?.octaves ?? 2,
    rootOctave: template?.rootOctave ?? 3,
    channel: template?.channel ?? 0,
    velocity: template?.velocity ?? 100,
    mode: template?.mode ?? 'momentary',
  };
  const proj = activeProject();
  if (proj) setActiveItemsAndNextCC([...pg.items, item], proj.nextCC);
  return item;
}

export function addLfo(originId?: string): LfoItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `lfo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is LfoItem => i.id === originId && i.kind === 'lfo')
    : undefined;
  const template = origin ?? pg.items.find((i): i is LfoItem => i.kind === 'lfo');

  const cc = p.nextCC;
  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('lfo', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: LfoItem = {
    id,
    kind: 'lfo',
    x, y, w, h,
    size,
    ccNumber: cc,
    channel: template?.channel ?? 0,
    bars: template?.bars ?? 1,
    min: template?.min ?? 0,
    max: template?.max ?? 127,
    waveform: template?.waveform ?? ('sine' satisfies LfoWaveform),
    color: template?.color ?? 'magenta',
    hue: 'none',
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(cc + 1));
  return item;
}

/**
 * Spawn a StatusLed. Templates off any existing LED so a series of
 * consecutive spawns walks up the CC axis (same behavior as button +
 * lfo). LEDs consume the same rolling CC counter — they're bound to the
 * incoming CC, so lining them up in the same CC space as buttons is the
 * common author intent ("light this LED when the button with the same
 * CC fires echo-back").
 */
export function addStatusLed(originId?: string): StatusLedItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `led-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const origin = originId
    ? pg.items.find((i): i is StatusLedItem => i.id === originId && i.kind === 'statusLed')
    : undefined;
  const template = origin ?? pg.items.find((i): i is StatusLedItem => i.kind === 'statusLed');

  const cc = p.nextCC;
  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('statusLed', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);

  const item: StatusLedItem = {
    id,
    kind: 'statusLed',
    x, y, w, h,
    size,
    color: template?.color ?? 'green',
    hue: 'none',
    sourceType: template?.sourceType ?? 'midi.cc',
    channel: template?.channel ?? 0,
    ccNumber: cc,
    threshold: template?.threshold ?? 1,
    shape: template?.shape ?? 'circle',
  };
  setActiveItemsAndNextCC([...pg.items, item], clampCC(cc + 1));
  return item;
}

/**
 * Add a Transpose row. Templates off any existing transpose for range +
 * color continuity. Defaults to -5..+7 (the user's rig range) with `0`
 * (home) active. Doesn't consume a CC — it dispatches OSC, not MIDI.
 * Width derives from cell size × semitone count.
 */
export function addTranspose(originId?: string): TransposeItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is TransposeItem => i.id === originId && i.kind === 'transpose')
    : undefined;
  const template = origin ?? pg.items.find((i): i is TransposeItem => i.kind === 'transpose');

  const id = `transpose-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const start = template?.start ?? -5;
  const end = template?.end ?? 7;
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const cell = sizeFor('transpose', size);
  const count = end - start + 1;
  const w = count * cell.w;
  const h = cell.h;
  const { x, y } = findFreeSpot(w, h);

  const item: TransposeItem = {
    id,
    kind: 'transpose',
    x, y, w, h,
    size,
    start,
    end,
    color: template?.color ?? 'amber',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Add a Capo row. Templates off any existing capo for range + color.
 * Defaults to 0..+7 (the user's range) — a guitar-side view of the shared
 * concert transpose. Range 0..+11; no CC (dispatches OSC via the transpose
 * session). Width derives from cell size × fret count.
 */
export function addCapo(originId?: string): CapoItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is CapoItem => i.id === originId && i.kind === 'capo')
    : undefined;
  const template = origin ?? pg.items.find((i): i is CapoItem => i.kind === 'capo');

  const id = `capo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const start = template?.start ?? 0;
  const end = template?.end ?? 7;
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const cell = sizeFor('capo', size);
  const count = end - start + 1;
  const w = count * cell.w;
  const h = cell.h;
  const { x, y } = findFreeSpot(w, h);

  const item: CapoItem = {
    id,
    kind: 'capo',
    x, y, w, h,
    size,
    start,
    end,
    color: template?.color ?? 'green',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Add a record-quantization row. Templates off any existing one for its
 * label set + colour, so a second row on another page matches the first
 * without re-picking six checkboxes.
 *
 * Width derives from cell size x label count, like transpose/capo.
 */
export function addRecQuant(originId?: string): RecQuantItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is RecQuantItem => i.id === originId && i.kind === 'recQuant')
    : undefined;
  const template = origin ?? pg.items.find((i): i is RecQuantItem => i.kind === 'recQuant');

  const id = `recquant-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const labels = template ? [...template.labels] : [...DEFAULT_REC_QUANT_LABELS];
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const quantizer = template?.quantizer;
  const { w, h } = recQuantDims(labels.length, quantizer, sizeFor('recQuant', size));
  const { x, y } = findFreeSpot(w, h);

  const item: RecQuantItem = {
    id,
    kind: 'recQuant',
    x, y, w, h,
    size,
    labels,
    color: template?.color ?? 'magenta',
    hue: 'none',
    ...(quantizer ? { quantizer } : {}),
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Re-pick which record-quantization labels a row offers, and reflow its
 * width. The incoming set is normalised through `REC_QUANT_LABELS` order
 * rather than taken as given, so the row always reads in Live's own enum
 * order no matter what order the Inspector checkboxes were tapped in - a
 * row that reorders itself under the operator is a row you cannot build
 * muscle memory on.
 *
 * An empty set is refused rather than accepted: a row with no cells is
 * indistinguishable from a broken item on the canvas, and unchecking the
 * last box is far more likely to be a slip than an intent. Delete the item
 * to get rid of it.
 */
export function setRecQuantLabels(id: string, wanted: readonly string[]): void {
  const set = new Set(wanted);
  const ordered = REC_QUANT_LABELS.filter((l) => set.has(l));
  if (ordered.length === 0) return;
  reflowRecQuant(id, (it) => ({ ...it, labels: [...ordered] }));
}

/**
 * Switch a row between Live's own record quantize and the OP-Quantize
 * device. OP-Quantize adds the Amount cell at the end of the row, so the row
 * grows by one cell rather than squeezing the pads - the pads are what gets
 * hit on stage. Leaving OP-Quantize drops what was last sent to it: those
 * values described a device the row no longer drives.
 */
export function setRecQuantQuantizer(id: string, via: RecQuantVia): void {
  reflowRecQuant(id, (it) => {
    if (via === 'op-quantize') return { ...it, quantizer: 'op-quantize' };
    const { quantizer: _q, sentLabel: _s, amount: _a, ...rest } = it;
    return rest;
  });
}

/** One row of cells: the labels, plus the Amount cell on OP-Quantize
 *  (2026-10-15; it replaced a band under the pads that made the row 1.45
 *  cells tall - `migrateV4toV5` resizes rows saved in that shape). */
export function recQuantDims(labelCount: number, quantizer: RecQuantVia | undefined, cell: { w: number; h: number }): { w: number; h: number } {
  const cells = Math.max(1, labelCount) + (quantizer === 'op-quantize' ? 1 : 0);
  return { w: cells * cell.w, h: cell.h };
}

/** Apply `change` to one rec-quant row and re-derive its size, kept on
 *  the canvas. */
function reflowRecQuant(id: string, change: (it: RecQuantItem) => RecQuantItem): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'recQuant') return it;
    const next = change(it);
    const { w, h } = recQuantDims(next.labels.length, next.quantizer, sizeFor('recQuant', next.size ?? 'M'));
    let x = next.x;
    let y = next.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...next, w, h, x, y } satisfies RecQuantItem;
  });
  setActiveItems(items);
}

// ─── Ableton Control: full rows and the One button / Full row switch ────
// DEC-291. The row model (controls, defaults, ordering) is `ableton/rows.ts`;
// these are its thin store wrappers.

/** One row of cells, `count` wide. */
export function abletonRowDims(count: number, cell: { w: number; h: number }): { w: number; h: number } {
  return { w: Math.max(1, count) * cell.w, h: cell.h };
}

/**
 * Add a full row of one Ableton Control. Templates off an existing row of
 * the SAME control on the page (its values, size and colour), the way a
 * second Rec Quantize row matches the first - a second tempo row on another
 * page should carry the presets already typed, not the defaults again.
 */
export function addAbletonRow(control: AbletonRowControl, originId?: string): AbletonRowItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is AbletonRowItem => i.id === originId && i.kind === 'abletonRow')
    : undefined;
  const ctl = origin?.control ?? control;
  const template = origin ?? pg.items.find((i): i is AbletonRowItem => i.kind === 'abletonRow' && i.control === ctl);
  const cells = template
    ? template.cells.map((c) => ({ ...structuredClone(c), id: ulid() }))
    : defaultRowCells(ctl);
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const { w, h } = abletonRowDims(cells.length, sizeFor('abletonRow', size));
  const { x, y } = findFreeSpot(w, h);
  const item: AbletonRowItem = {
    id: `abrow-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    kind: 'abletonRow',
    x, y, w, h,
    size,
    control: ctl,
    cells,
    color: template?.color ?? 'magenta',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Replace a row's cells and reflow its width, kept on the canvas. The set is
 * made sound first (`normaliseRowCells`: own step type, canonical order, one
 * cell per value). An empty result is REFUSED, as `setRecQuantLabels`
 * refuses one: clearing the last value is likelier a slip than an intent.
 */
export function setAbletonRowCells(id: string, cells: readonly AbletonStep[]): void {
  const pg = activePage();
  if (!pg) return;
  const it = pg.items.find((i): i is AbletonRowItem => i.id === id && i.kind === 'abletonRow');
  if (!it) return;
  const next = normaliseRowCells(it.control, cells);
  if (next.length === 0) return;
  const { w, h } = abletonRowDims(next.length, sizeFor('abletonRow', it.size ?? DEFAULT_SIZE));
  const bounds = usableCanvasBounds();
  let x = it.x;
  let y = it.y;
  if (bounds) {
    x = Math.max(0, Math.min(bounds.w - w, x));
    y = Math.max(0, Math.min(bounds.h - h, y));
  }
  setActiveItems(pg.items.map((i) => (i.id === id ? ({ ...it, cells: next, w, h, x, y } satisfies AbletonRowItem) : i)));
}

/** What an Ableton Control keeps across the One button / Full row switch:
 *  who it is (id, so the Inspector stays on it and undo is one step), where
 *  it is, how it looks, where it sends, and whether it is shared. */
function abletonCarry(it: LayoutItem): Record<string, unknown> {
  const src = it as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = { id: it.id, x: it.x, y: it.y, color: (src['color'] as string | undefined) ?? 'magenta' };
  for (const k of ['hue', 'name', 'hideName', 'nameSize', 'guard', 'bridgeIds', 'shareWithMods', 'controllerBound']) {
    if (src[k] !== undefined) out[k] = src[k];
  }
  return out;
}

/** Swap one item for its other form in place: same id, same top-left when
 *  the new size fits there, else nudged to the nearest clear spot - a row is
 *  wider than the button it came from and must not land on a neighbour. */
function swapInPlace(id: string, next: LayoutItem): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds() ?? activeCanvasSize() ?? { w: 1180, h: 752 };
  const others = pg.items.filter((i) => i.id !== id);
  let { x, y } = next;
  x = Math.max(0, Math.min(bounds.w - next.w, x));
  y = Math.max(0, Math.min(bounds.h - next.h, y));
  const r = { x, y, w: next.w, h: next.h };
  if (overlapsAny(r, others)) {
    const shift = nearestFreeShift([r], others, bounds);
    if (shift) { x += shift.dx; y += shift.dy; }
  }
  setActiveItems(pg.items.map((i) => (i.id === id ? ({ ...next, x, y } as LayoutItem) : i)));
}

/**
 * One button -> Full row. Only for an Ableton Control carrying ONE step of a
 * control that has a row; a chain ("140 BPM, 6/8, rec quant off") has no row
 * form. The button's own value is kept in the row. Record quantize becomes a
 * Rec Quantize row on the same quantizer.
 *
 * What a row cannot carry is dropped, and the Inspector says so BEFORE the
 * switch: a timeline step, MIDI trigger or Display pointed at the button.
 */
export function abletonButtonToRow(id: string): void {
  const pg = activePage();
  const it = pg?.items.find((i) => i.id === id);
  if (!it || it.kind !== 'button' || it.actionType !== 'ableton') return;
  const steps = it.abletonSteps ?? [];
  if (steps.length !== 1) return;
  const s = steps[0]!;
  const size: SizePreset = it.size ?? DEFAULT_SIZE;
  const keep = abletonCarry(it);
  if (s.type === 'ableton.recquant') {
    const own = normalizeRecQuant(s.label);
    const labels = REC_QUANT_LABELS.filter((l) => l === own || DEFAULT_REC_QUANT_LABELS.includes(l));
    const quantizer: RecQuantVia | undefined = s.via === 'op-quantize' ? 'op-quantize' : undefined;
    const { w, h } = recQuantDims(labels.length, quantizer, sizeFor('recQuant', size));
    swapInPlace(id, { ...keep, kind: 'recQuant', size, w, h, labels, ...(quantizer ? { quantizer } : {}) } as LayoutItem);
    return;
  }
  const control = rowControlForStep(s.type);
  if (!control) return;
  const cells = rowCellsFromStep(control, s);
  const { w, h } = abletonRowDims(cells.length, sizeFor('abletonRow', size));
  swapInPlace(id, { ...keep, kind: 'abletonRow', control, cells, size, w, h } as LayoutItem);
}

/**
 * Full row -> One button, carrying `pick`: the Inspector passes the value
 * Live is on when the row has it lit, else the row's first value, so the
 * button does what the row would most likely have been tapped for.
 */
export function abletonRowToButton(id: string, pick: AbletonStep): void {
  const pg = activePage();
  const it = pg?.items.find((i) => i.id === id);
  if (!it || (it.kind !== 'abletonRow' && it.kind !== 'recQuant')) return;
  const size: SizePreset = it.size ?? DEFAULT_SIZE;
  const dims = buttonSizeFor('rect', size);
  const keep = abletonCarry(it);
  const button: ButtonItem = {
    ...(keep as Pick<ButtonItem, 'id' | 'x' | 'y' | 'color'>),
    kind: 'button',
    w: dims.w, h: dims.h,
    size,
    ccNumber: 0,
    channel: 0,
    onValue: 127,
    shape: 'rect',
    rotation: 0,
    actionType: 'ableton',
    mode: 'trigger',
    abletonSteps: [{ ...structuredClone(pick), id: ulid(), target: '' }],
  };
  swapInPlace(id, button);
}

/**
 * Add a Scene Copy item. Templates off any existing one for range + color.
 * Defaults to scenes 1-5, matching the OP-AbletonSceneCopy device's own
 * hardwired SLOT_COUNT so the PWA surface and the device panel address the
 * same rows out of the box.
 *
 * Two columns (COPY | PASTE) wide, `count` rows tall - the transpose family
 * stacks horizontally, this one stacks vertically.
 */
export function addSceneCopy(originId?: string): SceneCopyItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is SceneCopyItem => i.id === originId && i.kind === 'sceneCopy')
    : undefined;
  const template = origin ?? pg.items.find((i): i is SceneCopyItem => i.kind === 'sceneCopy');

  const id = `scenecopy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const firstScene = template?.firstScene ?? 1;
  const count = template?.count ?? 5;
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const cell = sizeFor('sceneCopy', size);
  const showDeleteAll = template?.showDeleteAll ?? false;
  const { w, h } = sceneCopyDims(count, showDeleteAll, cell);
  const { x, y } = findFreeSpot(w, h);

  const item: SceneCopyItem = {
    id,
    kind: 'sceneCopy',
    x, y, w, h,
    size,
    firstScene,
    count,
    // Guarded by default: PASTE overwrites clips in the live set and there
    // is no OSC undo. Templating copies the operator's choice forward.
    guard: template?.guard ?? true,
    // Off unless the template item already had it on. A DELETE ALL bar is
    // never something a fresh item should arrive carrying - see
    // `SceneCopyItem.showDeleteAll`.
    showDeleteAll,
    color: template?.color ?? 'magenta',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Re-range a Scene Copy item. `firstScene` is the 1-based Live scene the
 * first row addresses (clamped 1..99 - Live sets are not unbounded but the
 * device takes any index, so this only stops a nonsense 0 or negative from
 * reaching the wire). `count` is clamped 1..8: below 1 there is nothing to
 * draw, and above 8 the item stops fitting an iPad canvas at M size.
 * Reflows height.
 */
export function setSceneCopyRange(id: string, firstScene: number, count: number): void {
  const pg = activePage();
  if (!pg) return;
  const f = Math.max(1, Math.min(99, Math.round(firstScene)));
  const c = Math.max(1, Math.min(8, Math.round(count)));
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'sceneCopy') return it;
    const cell = sizeFor('sceneCopy', it.size ?? 'M');
    const { w, h } = sceneCopyDims(c, it.showDeleteAll, cell);
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, firstScene: f, count: c, w, h, x, y } satisfies SceneCopyItem;
  });
  setActiveItems(items);
}

/**
 * Show or hide a Scene Copy item's DELETE ALL bar, reflowing its height.
 *
 * Its own action rather than a bare `editItem` patch because the bar CHANGES
 * THE ITEM'S SIZE (see `sceneCopyDims`) - a plain patch would leave `h`
 * describing a box the grid no longer fills, and the bar would render
 * squeezed into the slot rows instead of under them. Same reason
 * `setSceneCopyRange` exists rather than editing `count` directly.
 */
export function setSceneCopyDeleteAll(id: string, on: boolean): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'sceneCopy') return it;
    const cell = sizeFor('sceneCopy', it.size ?? 'M');
    const { w, h } = sceneCopyDims(it.count, on, cell);
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, showDeleteAll: on, w, h, x, y } satisfies SceneCopyItem;
  });
  setActiveItems(items);
}

/** Re-range a Capo row. Clamps start + end to [0, 11] (a capo is a
 *  non-negative fret), keeps end >= start, reflows width. */
export function setCapoRange(id: string, start: number, end: number): void {
  const pg = activePage();
  if (!pg) return;
  const s = Math.max(0, Math.min(11, Math.round(start)));
  let e = Math.max(0, Math.min(11, Math.round(end)));
  if (e < s) e = s;
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'capo') return it;
    const cell = sizeFor('capo', it.size ?? 'M');
    const w = (e - s + 1) * cell.w;
    const h = cell.h;
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, start: s, end: e, w, h, x, y } satisfies CapoItem;
  });
  setActiveItems(items);
}

/** Re-range a Transpose row. Clamps start to [-12, 0] and end to [0, 12]
 *  (the range always spans 0), reflows width from the new cell count, and
 *  keeps `current` in range (snaps to 0 if it fell outside). */
export function setTransposeRange(id: string, start: number, end: number): void {
  const pg = activePage();
  if (!pg) return;
  const s = Math.max(-12, Math.min(0, Math.round(start)));
  const e = Math.max(0, Math.min(12, Math.round(end)));
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'transpose') return it;
    const cell = sizeFor('transpose', it.size ?? 'M');
    const w = (e - s + 1) * cell.w;
    const h = cell.h;
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, start: s, end: e, w, h, x, y } satisfies TransposeItem;
  });
  setActiveItems(items);
}

/**
 * Add a Waves Autotune selector - two key rows + a scale-type row. Templates
 * off any existing one for range / homes / color. Defaults: -5..+7 offsets,
 * row 1 home A (Am), row 2 home E (Em). Dispatches MIDI CC (not OSC), so it
 * doesn't consume a project CC - the CC numbers live in autotune/session.ts.
 * Width = cell × key count; height = 3 rows.
 */
export function addWavesAutotune(originId?: string): WavesAutotuneItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is WavesAutotuneItem => i.id === originId && i.kind === 'wavesAutotune')
    : undefined;
  const template = origin ?? pg.items.find((i): i is WavesAutotuneItem => i.kind === 'wavesAutotune');

  const id = `wavesAutotune-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const start = template?.start ?? -5;
  const end = template?.end ?? 7;
  const placement = template?.scalePlacement ?? 'bottom';
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const cell = sizeFor('wavesAutotune', size);
  const { w, h } = wavesAutotuneDims(placement, start, end, cell);
  const { x, y } = findFreeSpot(w, h);

  const item: WavesAutotuneItem = {
    id,
    kind: 'wavesAutotune',
    x, y, w, h,
    size,
    start,
    end,
    row1Home: template?.row1Home ?? 9, // A → "Am"
    row2Home: template?.row2Home ?? 4, // E → "Em"
    channel: template?.channel ?? 2,   // matches DEFAULT_AUTOTUNE_CHANNEL
    scalePlacement: placement,
    color: template?.color ?? 'magenta',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Item dimensions for a Scene Copy: three columns (COPY / PASTE / CLEAR)
 * wide, `count` slot rows tall, plus the DELETE ALL bar when it is enabled.
 *
 * The bar GROWS the item rather than sharing the existing box, which is the
 * opposite of what `showStrength` does to a Waves Autotune. The reasoning is
 * the one that matters on a stage surface: squeezing five slot rows to make
 * room would shrink the controls the operator actually uses every set in
 * order to fit the one they hope never to press. Growing can push the item
 * against a neighbour, which is visible and draggable; shrinking every row
 * is invisible degradation. The bar is deliberately SHORTER than a slot row
 * (0.8) - it is not a scene, and it should not read as one more row.
 */
export const SCENE_COPY_DELETE_ALL_ROW = 0.8;

function sceneCopyDims(
  count: number,
  showDeleteAll: boolean | undefined,
  cell: { w: number; h: number },
): { w: number; h: number } {
  return {
    w: SCENE_COPY_COLUMNS * cell.w,
    h: count * cell.h + (showDeleteAll ? Math.round(cell.h * SCENE_COPY_DELETE_ALL_ROW) : 0),
  };
}

/** Item dimensions for a Waves Autotune selector, placement-aware.
 *  bottom/top: keyCount cells wide × 3 rows tall (2 key rows + a scale row).
 *  left/right: (keyCount + 1) cells wide × 2 rows tall (scale is a compact
 *  side column one cell wide). */
function wavesAutotuneDims(
  placement: WavesAutotuneItem['scalePlacement'],
  start: number,
  end: number,
  cell: { w: number; h: number },
): { w: number; h: number } {
  const count = end - start + 1;
  const side = placement === 'left' || placement === 'right';
  return side
    ? { w: (count + 1) * cell.w, h: 2 * cell.h }
    : { w: count * cell.w, h: 3 * cell.h };
}

/** v0.1 mixer bank size - the device (OP-AbletonMixer BANK_SIZE) mirrors the
 *  first 8 regular tracks. */
export const DEFAULT_MIXER_TRACKS = 8;

/** Item dimensions for a Mixer: N strips wide, one full strip tall
 *  (per-strip sizing, like buttongrid derives from rows × cols). */
function mixerDims(trackCount: number, cell: { w: number; h: number }): { w: number; h: number } {
  return { w: Math.max(1, trackCount) * cell.w, h: cell.h };
}

/** How many strips a Mixer renders given its mode: master = 1, returns =
 *  returnCount (default 2), regular = trackCount. Drives both sizing + render. */
export function effectiveMixerStrips(it: MixerItem): number {
  if (it.master) return 1;
  if (it.returns) return Math.max(1, Math.min(12, it.returnCount ?? 2));
  return Math.max(1, it.trackCount);
}

/**
 * Spawn a Mixer bank (DEC-M1). Composite channel-strip bank driving the
 * OP-AbletonMixer M4L hub over OSC - like Waves Autotune it dispatches to a
 * device (no project CC consumed), so it uses `setActiveItems`. v0.1 is a
 * fixed 8-track bank; live per-strip state lives in `mixer/session.ts`, not
 * on the item. Templates off any existing mixer for visual continuity.
 */
export function addMixer(originId?: string): MixerItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is MixerItem => i.id === originId && i.kind === 'mixer')
    : undefined;
  const template = origin ?? pg.items.find((i): i is MixerItem => i.kind === 'mixer');

  const id = `mixer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const master = template?.master ?? false;
  const returns = template?.returns ?? false;
  const returnCount = template?.returnCount ?? 2;
  const trackCount = template?.trackCount ?? DEFAULT_MIXER_TRACKS;
  const size: SizePreset = template?.size ?? DEFAULT_SIZE;
  const cell = sizeFor('mixer', size);
  const { w, h } = mixerDims(effectiveMixerStrips({ master, returns, returnCount, trackCount } as MixerItem), cell);
  const { x, y } = findFreeSpot(w, h);

  const item: MixerItem = {
    id,
    kind: 'mixer',
    x, y, w, h,
    size,
    trackCount,
    bankOffset: template?.bankOffset ?? 0,
    showMute: template?.showMute ?? true,
    showMeters: template?.showMeters ?? false,
    showNames: template?.showNames ?? true,
    // v0.2 stub controls — default off; inherit a template's choices for continuity.
    showSolo: template?.showSolo ?? false,
    showArm: template?.showArm ?? false,
    showPan: template?.showPan ?? false,
    showSends: template?.showSends ?? false,
    sendCount: template?.sendCount ?? 2,
    master,
    returns,
    returnCount,
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/** Toggle a Mixer's master-strip mode, reflowing w/h. Mutually exclusive with
 *  returns mode (enabling master clears returns). */
export function setMixerMaster(id: string, master: boolean): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'mixer') return it;
    const next = { ...it, master, returns: master ? false : it.returns === true } satisfies MixerItem;
    const cell = sizeFor('mixer', it.size ?? DEFAULT_SIZE);
    const { w, h } = mixerDims(effectiveMixerStrips(next), cell);
    return { ...next, w, h };
  });
  setActiveItems(items);
}

/** Toggle a Mixer's return-tracks-bank mode, reflowing w/h. Mutually exclusive
 *  with master mode (enabling returns clears master). */
export function setMixerReturns(id: string, returns: boolean): void {
  const pg = activePage();
  if (!pg) return;
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'mixer') return it;
    const next = { ...it, returns, master: returns ? false : it.master === true } satisfies MixerItem;
    const cell = sizeFor('mixer', it.size ?? DEFAULT_SIZE);
    const { w, h } = mixerDims(effectiveMixerStrips(next), cell);
    return { ...next, w, h };
  });
  setActiveItems(items);
}

/** Set how many return strips the returns-mode Mixer renders (1..12), reflowing. */
export function setMixerReturnCount(id: string, n: number): void {
  const pg = activePage();
  if (!pg) return;
  const returnCount = Math.max(1, Math.min(12, Math.round(n)));
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'mixer') return it;
    const next = { ...it, returnCount } satisfies MixerItem;
    const cell = sizeFor('mixer', it.size ?? DEFAULT_SIZE);
    const { w, h } = mixerDims(effectiveMixerStrips(next), cell);
    return { ...next, w, h };
  });
  setActiveItems(items);
}

/** Re-range a Waves Autotune selector. Clamps start to [-12, 0] and end to
 *  [0, 12] (range always spans 0 = home), reflows w/h (placement-aware). */
export function setWavesAutotuneRange(id: string, start: number, end: number): void {
  const pg = activePage();
  if (!pg) return;
  const s = Math.max(-12, Math.min(0, Math.round(start)));
  const e = Math.max(0, Math.min(12, Math.round(end)));
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'wavesAutotune') return it;
    const cell = sizeFor('wavesAutotune', it.size ?? 'M');
    const { w, h } = wavesAutotuneDims(it.scalePlacement ?? 'bottom', s, e, cell);
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, start: s, end: e, w, h, x, y } satisfies WavesAutotuneItem;
  });
  setActiveItems(items);
}

/** Set the scale-selector placement (bottom/top/left/right). Reflows w/h
 *  since side placements are 2 rows tall + a column wider. */
export function setWavesAutotunePlacement(
  id: string,
  placement: WavesAutotuneItem['scalePlacement'],
): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'wavesAutotune') return it;
    const cell = sizeFor('wavesAutotune', it.size ?? 'M');
    const { w, h } = wavesAutotuneDims(placement, it.start, it.end, cell);
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, scalePlacement: placement, w, h, x, y } satisfies WavesAutotuneItem;
  });
  setActiveItems(items);
}

/** Set the home/root pitch class (0-11) of row 1 or row 2 on a Waves
 *  Autotune selector. Normalises into 0-11. */
export function setWavesAutotuneHome(id: string, row: 1 | 2, pitchClass: number): void {
  const pg = activePage();
  if (!pg) return;
  const pc = (((Math.round(pitchClass) % 12) + 12) % 12);
  const items = pg.items.map((it) => {
    if (it.id !== id || it.kind !== 'wavesAutotune') return it;
    return row === 1
      ? ({ ...it, row1Home: pc } satisfies WavesAutotuneItem)
      : ({ ...it, row2Home: pc } satisfies WavesAutotuneItem);
  });
  setActiveItems(items);
}

export function removeItem(id: string): void {
  const pg = activePage();
  if (!pg) return;
  setActiveItems(pg.items.filter((it) => it.id !== id));
  // Drop any UI state still pointing at an item that no longer exists.
  // `selectedItem` is a memo over the page and self-clears, but the raw
  // `selectedItemId` would keep a dangling id that the next item added with
  // a recycled id could match.
  setSelectedIdsSig(selectedIdsSig().filter((s) => s !== id));
  if (moveArmedIdSig() === id) setMoveArmedIdSig(null);
  if (grabbedIdSig() === id) setGrabbedIdSig(null);
  if (pickButtonForSig() === id) setPickButtonForSig(null);
  clearMidiAllocFailure(id);
  dropClaimsOfItems([id]);
}

// ─── Item clipboard: cut / copy / paste / duplicate (2026-08-09) ──
// Backing store for the right-click menu's clipboard rows. Deliberately
// IN-MEMORY only, unlike the timeline's clipboard (`timeline/selection.ts`)
// which round-trips through `navigator.clipboard`:
//   * the menu must know synchronously whether to render Paste at all
//     ("only when cut or copied"), and a system-clipboard read is async and
//     permission-gated — it would make the row flicker or lie;
//   * a serialized layout item is not something anyone pastes into a text
//     editor, so the system clipboard buys nothing here.
// Survives page switches and project switches within the session; cleared
// by a reload. Cut keeps the payload so cut-then-paste-twice works.
//
// Holds an ARRAY since 2026-08-09 (multi-select): Cut/Copy act on the whole
// highlighted set, and a paste has to bring back the group with its shape
// intact, not a pile of items at one point.
const [itemClipboardSig, setItemClipboardSig] = createSignal<LayoutItem[]>([]);
/** True when there is something to paste. Drives the Paste row's presence. */
export const hasItemClipboard: Accessor<boolean> = () => itemClipboardSig().length > 0;

/**
 * Where a copied GROUP goes (2026-10-10, DEC-267; operator asked for copies
 * to land like new controls do). `box` is the group's bounding box where it
 * was copied from.
 *   - `at` (a paste at the point the menu was opened): the group's top-left
 *     there if it fits, else the nearest room to it.
 *   - otherwise: back in its own place if that is free (a cut, then a paste,
 *     puts it back), else beside the original a GAP away - right, a new row
 *     under it, below, left, above - the same rule a new control follows.
 * Replaced a 24px diagonal cascade, which stacked copies half over their
 * source and off the 10px grid. Falls back to the clamped spot when there is
 * no room at all: an overlapping copy can be dragged apart, an off-canvas one
 * cannot be reached.
 */
function copySpot(box: Rect, at?: { x: number; y: number }): { x: number; y: number } {
  const bounds = usableCanvasBounds() ?? activeCanvasSize() ?? { w: 1180, h: 752 };
  const items = activePage()?.items ?? [];
  const clampX = (v: number) => Math.max(0, Math.min(Math.max(0, bounds.w - box.w), v));
  const clampY = (v: number) => Math.max(0, Math.min(Math.max(0, bounds.h - box.h), v));
  if (at) {
    const x = clampX(at.x), y = clampY(at.y);
    const s = nearestFreeShift([{ x, y, w: box.w, h: box.h }], items, bounds, Math.max(bounds.w, bounds.h), controlGap());
    return s ? { x: x + s.dx, y: y + s.dy } : { x, y };
  }
  const home = { x: clampX(box.x), y: clampY(box.y), w: box.w, h: box.h };
  if (!overlapsAny(home, items)) return { x: home.x, y: home.y };
  return spawnSpot({ w: box.w, h: box.h }, items, bounds, { anchor: box, gap: controlGap() });
}

/** Put copies of these items on the clipboard. Leaves the page untouched. */
export function copyItems(ids: readonly string[]): void {
  const items = layoutItems();
  const picked = ids
    .map((id) => items.find((it) => it.id === id))
    .filter((it): it is LayoutItem => it !== undefined);
  if (picked.length === 0) return;
  setItemClipboardSig(picked.map((it) => structuredClone(it)));
}

/** Copy to the clipboard, then remove from the page. */
export function cutItems(ids: readonly string[]): void {
  copyItems(ids);
  if (itemClipboardSig().length === 0) return; // nothing matched - don't delete blind
  removeItems(ids);
}

/**
 * Commit a copied item onto the active page, renumbering whichever of its
 * MIDI slots the page already claims (see `midiAlloc.ts` for the rule and for
 * why "only what collides moves" gives Duplicate and Cut-then-Paste their two
 * different, both-correct behaviours).
 *
 * A failure to find a free slot is NOT a reason to refuse the paste. The
 * operator asked for a copy; they get one, on a colliding number, flagged
 * loudly - a red pulse on the item and a line in its inspector. Refusing
 * would leave them with nothing and no way to fix it by hand.
 */
function commitCopiedItem(pg: { items: LayoutItem[] }, clone: LayoutItem): LayoutItem {
  const { item, allocated, failure } = reallocateMidi(clone, pg.items);
  const items = [...pg.items, item];
  const highestCc = allocated.reduce<number | null>(
    (max, s: MidiSlot) => (s.space === 'cc' && (max === null || s.number > max) ? s.number : max),
    null,
  );
  if (highestCc === null) setActiveItems(items);
  // Keep the rolling counter past anything just handed out, or the next
  // freshly-added item spawns straight onto the CC this copy was given.
  else setActiveItemsAndNextCC(items, clampCC(highestCc + 1));
  if (failure) flagMidiAllocFailure(item.id, failure);
  return item;
}

/**
 * Drop fresh-id copies of the clipboard onto the active page. `at` is a
 * canvas-space top-left (the point the menu was opened at); without it the
 * copies go back where they were cut from, or beside where they were copied
 * from (`copySpot`). The clipboard is NOT
 * consumed - paste-twice is the expected behaviour.
 *
 * SHAPE IS PRESERVED for a multi-item paste. The group's bounding box is
 * anchored at the paste point and every item keeps its offset within it, so
 * pasting a row of five faders gives back a row of five faders. Only the
 * ANCHOR gets the free-spot search; running it per item would scatter them.
 */
export function pasteItems(at?: { x: number; y: number }): LayoutItem[] {
  const pg = activePage();
  const src = itemClipboardSig();
  if (!pg || src.length === 0) return [];
  // Group origin = top-left of the clipboard's bounding box.
  const box = boundsOf(src);
  const originX = box.x;
  const originY = box.y;
  const anchor = copySpot(box, at);
  const out: LayoutItem[] = [];
  for (const item of src) {
    const clone = cloneItemWithNewIds(structuredClone(item));
    const placed = {
      ...clone,
      x: anchor.x + (item.x - originX),
      y: anchor.y + (item.y - originY),
    } as LayoutItem;
    // Re-read the page each time: every commit appends, and the MIDI
    // allocator has to see the siblings pasted a moment ago or the whole
    // group lands on one CC.
    out.push(commitCopiedItem(activePage() ?? pg, placed));
  }
  return out;
}

/** Copy these items straight back onto the page, beside the originals
 *  (`copySpot`) and keeping their relative layout. Does not touch the clipboard -
 *  duplicating must not clobber whatever the operator copied a moment ago. */
export function duplicateItems(ids: readonly string[]): LayoutItem[] {
  const pg = activePage();
  if (!pg) return [];
  const src = ids
    .map((id) => pg.items.find((it) => it.id === id))
    .filter((it): it is LayoutItem => it !== undefined);
  if (src.length === 0) return [];
  const box = boundsOf(src);
  const originX = box.x;
  const originY = box.y;
  const anchor = copySpot(box);
  const out: LayoutItem[] = [];
  for (const item of src) {
    const clone = cloneItemWithNewIds(structuredClone(item));
    const placed = {
      ...clone,
      x: anchor.x + (item.x - originX),
      y: anchor.y + (item.y - originY),
    } as LayoutItem;
    out.push(commitCopiedItem(activePage() ?? pg, placed));
  }
  return out;
}

/**
 * Add a ButtonGrid. Defaults: 1 row × 10 cols, baseCC = rolling-CC,
 * channel 0, onValue 127, offValue 0. Cells auto-numbered "1".."10" with
 * the rotating color palette. The whole grid is sized to (cols × CELL_W)
 * by (rows × CELL_H); user can resize via the existing handle later.
 */
/** The colour a new grid takes: the first of the palette no grid on the page uses yet, else the one used least.
 *  Operator, 2026-10-26 ("yes one colour"), after public/button-look-study.html showed every grid on their page 1
 *  wearing the same automatic rainbow by position, so no grid could be told from another. Rainbow stays a choice. */
/** The order new grids take colours in. NOT the swatch row's wheel order: with 16 colours that would put orange right
 *  after red. The six wheel colours first (as before DEC-316's palette grew), then the nine new ones spread round the
 *  wheel so consecutive grids stay far apart, white last. Every palette colour is here exactly once. */
const NEW_GRID_ORDER: PaletteHue[] = [
  'red', 'yellow', 'green', 'cyan', 'blue', 'magenta',
  'orange', 'mint', 'violet', 'lime', 'sky', 'pink', 'teal', 'purple', 'amber', 'white',
];

function nextGridHue(items: readonly LayoutItem[]): PaletteHue {
  const order = NEW_GRID_ORDER;
  const used = new Map<PaletteHue, number>();
  for (const it of items) if (it.kind === 'buttongrid' && it.hue && it.hue !== 'none') used.set(it.hue, (used.get(it.hue) ?? 0) + 1);
  return order.find((h) => !used.has(h)) ?? order.reduce((a, b) => ((used.get(b) ?? 0) < (used.get(a) ?? 0) ? b : a));
}

export function addButtonGrid(rows = 1, cols = 10): ButtonGridItem | null {
  const p = activeProject();
  const pg = activePage();
  if (!p || !pg) return null;
  const id = `grid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const baseCC = p.nextCC;
  const size: SizePreset = DEFAULT_SIZE;
  const cell = sizeFor('buttongrid', size);
  const w = cols * cell.w;
  const h = rows * cell.h;
  const { x, y } = findFreeSpot(w, h);
  // ONE colour for the whole grid (its `hue`); the cells carry none of their own, as after setGridLook.
  const cells: ButtonGridCell[] = [];
  for (let i = 0; i < rows * cols; i++) {
    cells.push({
      id: `cell-${i}-${Math.random().toString(36).slice(2, 6)}`,
      label: String(i + 1),
    });
  }
  const item: ButtonGridItem = {
    id,
    kind: 'buttongrid',
    x, y, w, h,
    size,
    hue: nextGridHue(pg.items),
    rows, cols,
    baseCC,
    channel: 0,
    onValue: 127,
    offValue: 0,
    selectionMode: 'radio',
    maxActive: 2,
    // DEC-317: a new grid shows the bar-line wait by default (most launches wait for the bar, as Live's 1 Bar
    // quantisation does); a grid saved before this has no `starts` and keeps lighting at once.
    starts: 'bar',
    cells,
  };
  // Reserve baseCC..baseCC+rows*cols for this grid; next item starts past it.
  setActiveItemsAndNextCC([...pg.items, item], clampCC(baseCC + rows * cols));
  return item;
}

/**
 * Add a 4-row × 16-step drum sequencer onto the active page. Creates a
 * fresh Sequencer in the library (so each `+ Seq` press is a new
 * pattern, not an alias of an existing one) and a SequencerItem on the
 * page pointing at it. Notes default to a GM drum map (kick/snare/CH/OH)
 * on channel 10 (0-indexed 9), so the first basic drop-on-master-and-tap
 * test fires real drum sounds without any editor pass.
 */
export function addSequencer(): SequencerItem | null {
  const pg = activePage();
  if (!pg) return null;
  const seqDef = makeDrumSequencer();
  upsertSequencer(seqDef);
  const id = `sequencer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const size: SizePreset = DEFAULT_SIZE;
  const dims = sizeFor('sequencer', size);
  const { x, y } = findFreeSpot(dims.w, dims.h);
  const item: SequencerItem = {
    id,
    kind: 'sequencer',
    x, y, w: dims.w, h: dims.h,
    size,
    sequencerId: seqDef.id,
    color: 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Add a StreamDeck key trigger as a first-class layout item. Clones the
 * last SD item's config if present (sequential key indices), otherwise
 * seeds key=1 / cyan.
 */
export function addStreamDeck(originId?: string): StreamDeckItem | null {
  const pg = activePage();
  if (!pg) return null;
  const origin = originId
    ? pg.items.find((i): i is StreamDeckItem => i.id === originId && i.kind === 'streamdeck')
    : undefined;
  const template = origin ?? pg.items.find((i): i is StreamDeckItem => i.kind === 'streamdeck');

  const id = `sd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const size = template?.size ?? DEFAULT_SIZE;
  const dims = sizeFor('streamdeck', size);
  const w = template?.w ?? dims.w;
  const h = template?.h ?? dims.h;
  const { x, y } = findFreeSpot(w, h);
  // Spawns UNASSIGNED. There is no sensible default action to guess at:
  // actions are operator-chosen UUIDs from the Stream Deck MCP Actions
  // profile, so the item asks to be pointed at one via the Inspector
  // rather than silently pointing at someone else's key.
  const item: StreamDeckItem = {
    id,
    kind: 'streamdeck',
    x, y, w, h,
    size,
    // `cyan`, not `amber`, as of 2026-07-30 - operator: "streamdeck buttons
    // seem to be locked into yellow? they need to act like normal buttons
    // (colour-wise)". A normal button spawns cyan; this was the ONE kind
    // seeded amber. Post-DEC-127 that legacy value is no longer a state
    // token - `hueOf()` migrates it to the `yellow` hue on read - so the
    // seed silently became "every Stream Deck button is born yellow", with
    // no way to tell that apart from a locked style. Fixing the CSS alone was
    // not enough: the amber CSS base and the amber seed were two independent
    // causes of the same yellow, and the second one survived the first fix.
    color: template?.color ?? 'cyan',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Add a Macro tile referencing a macro from the library. If the library
 * is empty, auto-creates a fresh "Macro 1" stub (one sine lane on
 * CC 72) and points the new item at it. Otherwise references the first
 * existing macro — user can swap via the ⋯ → "Edit macros" affordance.
 */
export function addMacro(): MacroItem | null {
  const pg = activePage();
  if (!pg) return null;
  let macroId = macros()[0]?.id;
  if (!macroId) {
    const fresh: Macro = {
      id: ulid(),
      name: 'Macro 1',
      triggerLengthBeats: 8,
      lanes: [
        {
          id: ulid(),
          label: 'CC 72',
          target: { bridge: 'pc', kind: 'cc', channel: 0, number: 72 },
          lengthBeats: 8,
          shape: { kind: 'sine' },
          center: 64,
          amplitude: 63,
        },
      ],
    };
    upsertMacro(fresh);
    macroId = fresh.id;
  }
  const id = `macro-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const size: SizePreset = DEFAULT_SIZE;
  const dims = sizeFor('macro', size);
  const { x, y } = findFreeSpot(dims.w, dims.h);
  const item: MacroItem = {
    id,
    kind: 'macro',
    x, y, w: dims.w, h: dims.h,
    size,
    macroId,
    color: 'magenta',
    hue: 'none',
  };
  setActiveItems([...pg.items, item]);
  return item;
}

/**
 * Resize a ButtonGrid's row × col shape. Preserves existing cells whose
 * (row, col) still fits in the new shape; appends fresh cells for any
 * new positions; drops cells that fall outside. Cell ids are stable so
 * per-cell overrides survive a resize.
 */
/** Audio pads: change the grid, keeping the pads that still fit (in reading order) and adding empty ones. */
export function resizeAudioPads(itemId: string, rows: number, cols: number): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds();
  setActiveItems(pg.items.map((it) => {
    if (it.id !== itemId || it.kind !== 'audioPads') return it;
    const total = rows * cols;
    const pads: AudioPad[] = it.pads.slice(0, total);
    for (let i = pads.length; i < total; i++) pads.push({ color: CELL_COLOR_PALETTE[i % CELL_COLOR_PALETTE.length] ?? 'cyan' });
    const cell = sizeFor('audioPads', it.size ?? 'M');
    const w = cols * cell.w;
    const h = rows * cell.h;
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return { ...it, x, y, rows, cols, w, h, pads } satisfies AudioPadsItem;
  }));
}

/** Cells a shrink cut off, per grid, so growing back brings them back
 *  with their names, CCs and colours (operator, 2026-10-15: "i labelled 10
 *  cells in a button grid, and then add 1 more and it reset all but the 1
 *  cell names" - retyping Cols 10 as 11 passed through 1). Session only:
 *  the saved grid holds exactly rows x cols cells, as everything reading
 *  it expects. Holds the grid's longest cell list, by position. */
const trimmedGridCells = new Map<string, ButtonGridCell[]>();

export function resizeButtonGrid(gridId: string, rows: number, cols: number): void {
  const pg = activePage();
  if (!pg) return;
  const bounds = usableCanvasBounds();
  const items = pg.items.map((it) => {
    if (it.id !== gridId || it.kind !== 'buttongrid') return it;
    const total = rows * cols;
    // Everything this grid has held, by position: its cells now (with any
    // edits), then what earlier shrinks cut off beyond them.
    const known = [...it.cells, ...(trimmedGridCells.get(gridId) ?? []).slice(it.cells.length)];
    const existing = known.slice(0, total);
    if (known.length > total) trimmedGridCells.set(gridId, known);
    else trimmedGridCells.delete(gridId);
    // A grid in ONE colour (setGridLook cleared its cells' own) grows in that colour; only a rainbow grid continues
    // the rainbow. Until 2026-10-26 every new cell took the next rainbow colour, so a green grid grown by two columns
    // ended cyan and magenta (smoke button_grid_whole_grid_look).
    const oneColour = (it.hue !== undefined && it.hue !== 'none') || (existing.length > 0 && existing.every((c) => !c.color));
    const additions: ButtonGridCell[] = [];
    for (let i = existing.length; i < total; i++) {
      additions.push({
        id: `cell-${i}-${Math.random().toString(36).slice(2, 6)}`,
        label: String(i + 1),
        ...(oneColour ? {} : { color: CELL_COLOR_PALETTE[i % CELL_COLOR_PALETTE.length] ?? 'cyan' }),
      });
    }
    const cell = sizeFor('buttongrid', it.size ?? 'M');
    const w = cols * cell.w;
    const h = rows * cell.h;
    // Reposition if the new bounds spill past the canvas edge — same
    // shape as EditableItem's drag clamp. Growing rows on a grid near
    // the bottom used to bleed out of bounds; growing cols on a grid
    // near the right edge had the same trap. Shrinking never triggers
    // this branch (x/y stay valid).
    let x = it.x;
    let y = it.y;
    if (bounds) {
      x = Math.max(0, Math.min(bounds.w - w, x));
      y = Math.max(0, Math.min(bounds.h - h, y));
    }
    return {
      ...it,
      x, y,
      rows, cols,
      w, h,
      cells: [...existing, ...additions],
    } satisfies ButtonGridItem;
  });
  setActiveItems(items);
}

/** Read the actual pixel bounds of `.scaled-canvas-inner` — the region
 *  where items are absolutely positioned. This differs from
 *  activeCanvasSize (which returns device dims like iPad 1180×820)
 *  because the shell reserves space for header + footer chrome, so the
 *  canvas region is smaller (typically ~68px shorter). Uses the same
 *  element EditableItem's drag arbiter reads bounds from, so clamp
 *  behavior is consistent. Returns null if the element isn't in the
 *  DOM (e.g. store mutation happens outside a render frame). */
function usableCanvasBounds(): { w: number; h: number } | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('.scaled-canvas-inner') as HTMLElement | null;
  if (!el) return null;
  return { w: el.clientWidth, h: el.clientHeight };
}

function clampCC(n: number): number {
  if (n > 127) return 0;
  if (n < 0) return 127;
  return n | 0;
}

// ─── Where a new control goes (2026-10-10) ──────────────────────
// A point the operator chose for the NEXT add: the canvas menu's "+ Item"
// sets it to where they right-clicked, a tile dragged out of the palette to
// where it was let go. Cleared when the palette closes, so it never leaks
// into a later, unrelated add.
let spawnPoint: { x: number; y: number } | null = null;
export function setSpawnPoint(p: { x: number; y: number } | null): void { spawnPoint = p; }

// DEV test hook (2026-10-10). The bot opens the palette through the canvas
// menu's "+ Item", which now adds at the right-clicked point; smokes that are
// not about placement clear it so their controls get the ordinary spot
// instead of piling up wherever the bot happened to right-click. Stripped
// from production by the `import.meta.env.DEV` gate.
declare global {
  interface Window { __opbuttonsLayout?: { setSpawnPoint: typeof setSpawnPoint } }
}
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsLayout = { setSpawnPoint };
}

/**
 * Where a new item of this size goes (`placement.ts` `spawnSpot`): at the
 * spawn point if one is set, else beside the selected control, else beside
 * the last one added, a GAP away; the top-left margin on an empty page.
 * Before 2026-10-10 this scanned down the left column from (40,40), so every
 * add landed packed against the last in the top-left corner, wherever the
 * operator was working, and on a full page on top of whatever was there.
 */
function findFreeSpot(w: number, h: number): { x: number; y: number } {
  // The MEASURED canvas region, not the device dims. `activeCanvasSize` is
  // the whole 1180x820 frame including the header, so bounding by it let a
  // spawn land up to ~68px below the visible canvas - measured 2026-09-24: an
  // OPVisual Trigger added under a full-height Grid spawned half off the
  // bottom of the iPad frame. `copySpot` uses the same pair.
  const bounds = usableCanvasBounds() ?? activeCanvasSize() ?? { w: 1180, h: 752 };
  const items = activePage()?.items ?? [];
  const selected = selectedItemsOnPage();
  const anchor = selected[selected.length - 1] ?? items[items.length - 1] ?? null;
  return spawnSpot({ w, h }, items, bounds, { point: spawnPoint, anchor, gap: controlGap() });
}

// Items added a moment ago, for the canvas to pulse once so the eye finds
// them. Highlighted too (no inspector), so the next drag or Delete acts on
// exactly what just appeared.
const [justAddedSig, setJustAddedSig] = createSignal<ReadonlySet<string>>(new Set());
let justAddedTimer: ReturnType<typeof setTimeout> | undefined;
export function isJustAdded(id: string): boolean { return justAddedSig().has(id); }
export function announceAdded(ids: readonly string[]): void {
  if (ids.length === 0) return;
  setSelectedItems(ids);
  setJustAddedSig(new Set(ids));
  if (justAddedTimer !== undefined) clearTimeout(justAddedTimer);
  justAddedTimer = setTimeout(() => setJustAddedSig(new Set<string>()), 900);
}
/** Run an add and announce whatever it put on the page. Diffing ids rather
 *  than threading a return value through thirty-odd add functions, so a kind
 *  added later is covered without knowing about this. */
export function addAndAnnounce(add: () => void): void {
  const before = new Set(layoutItems().map((it) => it.id));
  add();
  announceAdded(layoutItems().filter((it) => !before.has(it.id)).map((it) => it.id));
}

/**
 * AABB overlap check for collision/snapback. `excludeId` lets the moving
 * item ignore itself.
 */
export function wouldOverlap(
  x: number, y: number, w: number, h: number,
  excludeId?: string,
): boolean {
  const items = activePage()?.items ?? [];
  for (const it of items) {
    if (it.id === excludeId) continue;
    const overlapX = x < it.x + it.w && x + w > it.x;
    const overlapY = y < it.y + it.h && y + h > it.y;
    if (overlapX && overlapY) return true;
  }
  return false;
}

/**
 * Group-drag version: would ANY of these placements land on an item outside
 * the moving set? The whole set is excluded rather than one id, because the
 * members keep their relative positions during a group drag and so cannot
 * newly collide with each other - checking them against each other would
 * reject every legal drag of two items that were already touching.
 */
export function groupWouldOverlap(
  placements: readonly { x: number; y: number; w: number; h: number }[],
  excludeIds: ReadonlySet<string>,
): boolean {
  const items = activePage()?.items ?? [];
  for (const p of placements) {
    for (const it of items) {
      if (excludeIds.has(it.id)) continue;
      const overlapX = p.x < it.x + it.w && p.x + p.w > it.x;
      const overlapY = p.y < it.y + it.h && p.y + p.h > it.y;
      if (overlapX && overlapY) return true;
    }
  }
  return false;
}

// ─── Live group-drag offset ──────────────────────────────────────
// While several items drag together, only the item under the pointer runs a
// drag handler. The others need to render offset too, and they cannot be
// written to the store per frame - that replaces the item objects and makes
// App's `<For each={layoutItems()}>` recreate every row mid-drag, which is
// the DEC-119/120 trap that once killed the fader after one pointer event.
// So the delta lives here as pure view state and each EditableItem adds it to
// its own x/y while its id is in the set. Committed once, on drop.
//
// Since 2026-10-10 EVERY member of a drag, the item under the pointer
// included, renders through this offset as a CSS `translate` from its stored
// spot, so a drag moves pixels on the compositor rather than re-laying-out
// the page each pointer move. `settling` is the ease into the final spot
// after the drop: the store already holds the final x/y and the offset runs
// from where the item was let go down to zero.
export interface GroupDrag { ids: string[]; dx: number; dy: number; settling?: boolean }
const [groupDragSig, setGroupDragSig] = createSignal<GroupDrag | null>(null);
export const groupDrag: Accessor<GroupDrag | null> = groupDragSig;
export function setGroupDrag(v: GroupDrag | null): void {
  setGroupDragSig(v);
}

// What the canvas draws while something is dragged or resized: the snap
// guides, and - while the drop would land on another control - the outlines
// of where it will settle instead (`blocked` with no ghosts = it will go back
// to where it started). Pure view state, cleared on release.
export interface DragOverlay { guides: Guide[]; ghosts: Rect[]; blocked: boolean }
const [dragOverlaySig, setDragOverlaySig] = createSignal<DragOverlay | null>(null);
export const dragOverlay: Accessor<DragOverlay | null> = dragOverlaySig;
export function setDragOverlay(v: DragOverlay | null): void { setDragOverlaySig(v); }

// ─── DEV test hook ───────────────────────────────────────────────────────────
// Added 2026-08-17 to reproduce a real operator symptom (TESTSONG: a Song
// timeline with Helix PresetChange steps producing no toast at all on
// Launch) — every Timeline dispatch smoke up to this point used a fresh
// synthetic Button, never a real pedal-bound PresetChange item, so this is
// the first way to build that exact shape from a bot. Mirrors
// `timeline/store.ts`'s own `__opbuttonsTimeline` hook. Stripped from
// production by the `import.meta.env.DEV` gate.
declare global {
  interface Window {
    __opbuttonsPedal?: {
      /** Registers a pedal + a bound PresetChange item in one call. Returns
       *  both ids, or `null` if there's no active project/page. */
      addHelixPedalAndPresetChange(opts: {
        port: string;
        channel: number;
        bridgeId?: string;
        setlist?: number;
        program?: number;
      }): { pedalId: string; presetChangeItemId: string } | null;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsPedal = {
    addHelixPedalAndPresetChange: (opts) => {
      const pedal = addPedal({
        name: 'Bot Test Helix',
        modelId: 'helix-floor',
        type: 'helix',
        port: opts.port,
        channel: opts.channel,
        ...(opts.bridgeId !== undefined ? { bridgeId: opts.bridgeId } : {}),
      });
      const item = addPresetChangeForPedal(pedal);
      if (!item) return null;
      if (opts.setlist !== undefined || opts.program !== undefined) {
        setActiveItems(
          (activeProject()?.pages.find((p) => p.id === activePage()?.id)?.items ?? []).map((i) =>
            i.id === item.id
              ? { ...i, setlist: opts.setlist ?? (i as PresetChangeItem).setlist, program: opts.program ?? (i as PresetChangeItem).program }
              : i,
          ),
        );
      }
      return { pedalId: pedal.id, presetChangeItemId: item.id };
    },
  };
}
